import React from "react";
import {Bell, Menu, MessageCircleReply, MessagesSquare, SquarePen} from "@/lib/eagerIcons";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import HeaderButton from "@/main/react/buttons/HeaderButton";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {SidebarProvider} from "@/components/ui/sidebar";
import {Localizer} from "@/main/react/Types";
import {MainSideBarInner, MainSideBarViewProps} from "@/main/react/header/sidebar/MainSideBar";
import type {NotificationsDrawerOpenRequest} from "@/main/react/header/sidebar/notification/NotificationsView";
import LogoIcon from "@/components/customsvg/LogoIcon";
import HeaderSearchBar from "@/main/react/header/HeaderSearchBar";
import {createReactMount, type ReactMount} from "@/main/react/mountContract";
import {captureSsrSnapshot} from "@/main/react/ssrSnapshot";
import {LazyControlBoundary} from "@/main/react/LazyControlBoundary";
import {ACCOUNT_ACCESS_DIALOG_COPY, resolveContextualAccountAccessDialogOptions} from "@/main/react/account_access/AccountAccessDialogContext";
// FI-2604: the lazy opener, so the dialog shell stays off the eager graph.
import {openAccountAccessDialog} from "@/main/react/account_access/openAccountAccessDialogLazy";
import { emitOpenChatDrawer, onOpenChatDrawer, onProfileUpdated, type OpenChatDrawerPayload } from "@/main/common/EventRegistry";
import {BROWSER_NAVIGATION_EVENT_NAME, navigateToUrl} from "@/main/common/BrowserNavigation";
import { actionableNotificationCount, conversationsNotificationBadge, hasPassiveUnreadNotifications, messagesBadge } from "@/main/headerInbox/HeaderInboxModels";
import { refreshHeaderInbox } from "@/main/headerInbox/HeaderInboxStore";
// The decision predicate ONLY. ⚠️ Never import it from `NotificationItem` — that file is the head of
// the 581.5 KB closure this split removed, and the edge would silently restore it.
import { hasPendingDecisions } from "@/main/react/header/sidebar/notification/notificationDecision";
import { useHeaderInbox } from "@/main/headerInbox/useHeaderInbox";
import { getShellStateSnapshot, subscribeToShellState } from "@/main/navigation/ShellStateStore";
import {
    buildMessagesUrl,
    focusMessagesPageInbox,
    isOnMessagesPagePath,
    namesAConversation,
    useDedicatedMessagesPagePreference,
} from "@/main/react/header/sidebar/chat/chatNavigation";
import {retapBottomNavTab} from "@/main/react/header/bottomNavTabRetap";
import {useCurrentPathname} from "@/main/react/hooks/useCurrentPathname";
import {
    getCurrentRelativeUrl,
    readNotificationContinuationPrompt,
    type ParsedNotificationContinuationPrompt,
} from "@/main/react/account_access/AccountAccessContinuation";
import {isSoftKeyboardOpen, useKeyboardInsetPx} from "@/hooks/use-keyboard-inset";
import {REPLY_COMPOSER_ROOT_ATTRIBUTE} from "@/lib/replyComposerRootAttribute";
import {useMediaQueryMatches} from "@/main/react/hooks/useMediaQueryMatches";
import {MobileBottomNav, SEARCH_TAB_HREF, getMobileBottomNavVisibility} from "@/main/react/header/MobileBottomNav";
import { focusSearchPageField } from "@/main/react/header/searchPageField";
import {importChunkWithRetry, lazyWithRetry} from "@/lib/lazyWithRetry";
import {ensureDeferredCatalogLoaded, ensureSurfaceCatalogLoaded} from "@/main/messages/deferredCatalog";
// `isStandaloneAccountAccessPath` moved to a shared module (U6); `/reset_password/` joined it (A14).
import {isStandaloneAccountAccessPath} from "@/main/navigation/accountAccessRoutes";
import {probeServerReachable} from "@/main/util/probeServerReachable";
import {MobileShellRuntime} from "@/main/mobile-shell/MobileShellRuntime";
import {prefersReducedMotion} from "@/main/util/motionPreference";
import {
    replaceInPageHistoryUrl,
    replaceWithSurfaceThatServesIt,
} from "@/main/navigation/NavigationHistoryState";
import { VIEWPORT_CONTRACT_ATTRIBUTE } from "@/main/navigation/NavigationRootShell";
import { isPhoneViewportNow, MOBILE_VIEWPORT_QUERY_768 } from "@/hooks/use-mobile";
import {
    SCROLL_TUCK_HIDE_THRESHOLD_PX,
    SCROLL_TUCK_REVEAL_THRESHOLD_PX,
    SCROLL_TUCK_TOP_ZONE_PX,
    trackUserScrollIntent,
} from "@/main/react/header/scrollTuckThresholds";

const CONNECTION_BANNER_HEIGHT_PX = 36;
const CONNECTION_BANNER_HEIGHT_CSS = `${CONNECTION_BANNER_HEIGHT_PX}px`;
// This var carries the combined height of the fixed top-banner stack (connection/session banner
// plus the verify-email nudge banner). The JTE main-content top padding, the header top offset and
// hide/show transform, and the mobile-header padding all consume it so page content is pushed below
// whatever banners are showing instead of being overlaid by them.
const CONNECTION_BANNER_CSS_VAR = "--seavien-connection-banner-height";
/*
 * S7 A87 (`DISCOVERY-57`): the offset the header ACTUALLY occupies below the banner stack's top, published
 * on `<html>` by the visibility effect below. A sticky element that must sit just under the header (the
 * `/search` filler-column label) reads `var(--seavien-header-offset, <its old fixed value>)` and adds
 * `env(safe-area-inset-top)` itself, so this value carries no safe-area term. Visible: the header plus the
 * banner stack; tucked (scroll, keyboard or chat-thread tuck alike): `0px`. Removed on unmount so every
 * consumer falls back to its own default.
 */
const HEADER_OFFSET_CSS_VAR = "--seavien-header-offset";
// Bottom-edge analogue of the connection-banner offset: the floating verify-email nudge publishes
// its measured height here so `<main>` can reserve matching `padding-bottom`, keeping bottom-anchored
// controls (change-password / delete-account save buttons, full-width under the card on mobile) clear
// of the fixed card. 0px whenever the nudge is absent or dismissed. See the 2026-06-10 nudge design.
const MOBILE_HEADER_VISIBLE_HEIGHT = "4.5rem";
const MOBILE_HEADER_TRANSITION_DURATION_MS = 240;
const MOBILE_HEADER_HIDDEN_TRANSLATE_Y = `translate3d(0, calc(-100% - var(${CONNECTION_BANNER_CSS_VAR}, 0px) - 1px), 0)`;
const MOBILE_HEADER_VISIBLE_TRANSLATE_Y = "translate3d(0, 0, 0)";
/*
 * DISCOVERY-165 (r179): the default shell's ONE page-heading top spacing, `1rem`, on top of the header's `4.5rem`
 * (`basic_layout.jte` `md:pt-[calc(5.5rem+…)]`, `NavigationRootShell.ts`). The visible reserve below is written INLINE,
 * which outranks the template's class, so it must carry the same term: without it every default-layout page jumped
 * 16 px up at mount (devo gate `2157e3209a`, `discussion-h1-layout-invariance.spec.mjs`).
 */
const PAGE_HEADING_TOP_SPACING = "1rem";
const MOBILE_HEADER_VISIBLE_PADDING_TOP = `calc(${MOBILE_HEADER_VISIBLE_HEIGHT} + ${PAGE_HEADING_TOP_SPACING} + env(safe-area-inset-top) + var(${CONNECTION_BANNER_CSS_VAR}, 0px))`;
const MOBILE_HEADER_HIDDEN_PADDING_TOP = `calc(env(safe-area-inset-top) + var(${CONNECTION_BANNER_CSS_VAR}, 0px))`;
/**
 * SV-157 A3 (2026-10-05): **the phone has no top bar, so its top reserve is the safe area plus the banner stack**, and
 * since DISCOVERY-165 at least the `1rem` heading spacing: `max(inset, 1rem)`, the template's
 * `pt-[calc(max(env(safe-area-inset-top),1rem)+banner)]` below `md`, so the inline write and the template agree.
 */
const PHONE_TOP_RESERVE_PADDING_TOP = `calc(max(env(safe-area-inset-top), ${PAGE_HEADING_TOP_SPACING}) + var(${CONNECTION_BANNER_CSS_VAR}, 0px))`;
// r156 (MAF-19, ⚑G-35): the phone bottom bar tucks on the same numbers, so they live in one shared module.
const MOBILE_HEADER_SCROLL_HIDE_THRESHOLD_PX = SCROLL_TUCK_HIDE_THRESHOLD_PX;
const MOBILE_HEADER_SCROLL_REVEAL_THRESHOLD_PX = SCROLL_TUCK_REVEAL_THRESHOLD_PX;
const MOBILE_HEADER_TOP_ZONE_PX = SCROLL_TUCK_TOP_ZONE_PX;

/**
 * What the mobile header does on a route.
 *
 * <p>⚠️ There used to be a third member, `"lockVisible"`, returned for `/create/discussion`. **Nothing
 * ever read it.** `syncVisibility` compares the policy against `"visibleOnly"` and against nothing
 * else, so a route "locked visible" fell through to exactly the scroll logic the feeds use. It was
 * deleted with `CREATE-PUBLISH-23` (wave-14 Q29) rather than wired up, because a policy value the
 * runtime does not honour is worse than no policy: `CREATE-PUBLISH-23` was filed against it by name,
 * and the two things that actually keep the composer's header on screen are elsewhere — see
 * `getMobileHeaderVisibilityPolicy`.
 */
type MobileHeaderVisibilityPolicy = "hideOnScroll" | "visibleOnly";

function readWindowScrollTop(): number {
    if (typeof window === "undefined") {
        return 0;
    }

    return Math.max(
        0,
        Math.round(window.scrollY || document.scrollingElement?.scrollTop || document.documentElement?.scrollTop || 0),
    );
}

function isEditableElement(element: Element | null): boolean {
    if (!element) {
        return false;
    }

    if ((element as HTMLElement).isContentEditable) {
        return true;
    }

    const tagName = element.tagName.toLowerCase();
    return tagName === "input" || tagName === "textarea" || tagName === "select";
}

/**
 * Is the header pinned visible because the member is in the middle of something?
 *
 * <p>Exactly one reason today: focus is in an editable element. It used to be two — the other was
 * `hasOpenTopLayerModal()`, "a modal is open" — but nothing in the product ever registered a modal, so
 * that clause read a constant `false` and never fired. It was deleted on 2026-09-04 with the rest of the
 * inert registry (`FQ-F3` / `BL-122`), which is a removal of dead code rather than of behaviour.
 *
 * <p>⚠️ So the header CAN still auto-hide on scroll while a dialog is open, and always could. If that
 * turns out to matter, it is a new feature and needs a real open-dialog predicate — not a resurrection
 * of the old one.
 */
function isLockedVisibleSurface(rootDocument: Document): boolean {
    return isEditableElement(rootDocument.activeElement);
}

/** The chat page, matched the same way `getMobileHeaderVisibilityPolicy` matches it. */
const MESSAGES_PATHNAME = "/messages";

/** A discussion page, matched the way `MobileBottomNav` (D8) matches it. */
const DISCUSSION_PATH_PREFIX = "/discussion/";

/** The composer's entry path. Mirrors `navigation/composerRoutes.ts`; see `isOnComposerPath`. */
const CREATE_DISCUSSION_PATH = "/create/discussion";

/**
 * `CREATE-PUBLISH-10` / operator decision D7: what "start another" resolves to.
 *
 * <p>`CreateController.getCreateDiscussionAndCreateANewOne` renders the composer shell in
 * `forceCreateNew` mode, so this GET mints a brand-new draft every visit - as opposed to
 * `/create/discussion`, which is `reuseEmptyOrCreate` and hands back the empty draft the author is
 * already sitting in. ⛔ The route is **not** retired: `DiscussionDraftsDialog`'s `openInNewTab`
 * fallback reaches it from two live call sites.
 */
const CREATE_DISCUSSION_FORCE_NEW_PATH = "/create/discussion/new";

/**
 * Stage 6 (2026-08-12, owner decision): while a soft keyboard is open on the chat page, the site header
 * stands down and gives its 4.5rem reserve back to the conversation.
 *
 * With the keyboard up the visible viewport is barely two bubbles tall, and the page was spending that
 * reserve on a site header stacked above the thread's own header — two headers where WhatsApp shows one.
 * The thread's own header, with its back arrow, is inside the island and always stays, so the
 * conversation never loses its way out.
 *
 * **This has to live here, and the first attempt to put it in a stylesheet did not work.** A CSS rule
 * hiding the header and zeroing `#seavien-main-content`'s padding was correct on specificity and layer
 * order, and lost anyway — `updateMainContentPadding` writes that padding as an INLINE style, which no
 * stylesheet rule can outrank. The visible result was the worst kind of half-fix: the header disappeared
 * and its 72px gap stayed. So the keyboard is expressed as one more input to the visibility runtime that
 * already owns both the transform and the padding, which also gets the 240ms tween and the
 * reduced-motion branch for free.
 *
 * Takes precedence over `isLockedVisibleSurface` deliberately: that lock exists so typing into a form
 * does not tuck the header away, and here the focused field is exactly the reason to tuck it away.
 *
 * `readKeyboardInsetPx` rather than a local re-derivation — see AGENTS.md rule 25 and the two drifted
 * copies of it that used to live in `ChatExperience`.
 */
/**
 * The threshold is `SOFT_KEYBOARD_MIN_INSET_PX` (120px) in `hooks/use-keyboard-inset.ts`, via
 * `isSoftKeyboardOpen`. It was a local constant here until 2026-09-22; it moved when the reply
 * composer's viewport ceiling needed the same question answered, so the two cannot drift. Why `> 0`
 * was wrong (it hid the header on DESKTOP, owner report 2026-08-16) is recorded beside it.
 *
 * **2026-09-22 (DISCUSSION-103, operator-approved): the tuck also applies to a focused REPLY composer
 * on a discussion page.** With the keyboard up, the site header, the stance chips and a two-row toolbar
 * left the reply 136 of 578 CSS px. The reason Stage 6 gives for outranking the focus lock holds there
 * word for word, so it is the same tuck with one more surface, not a second mechanism. Matched by path
 * AND by focus being inside `[data-reply-composer-root]` (the attribute `ThoughtTextEditor` puts on its
 * root), so a search box or a report form on the same page keeps the header. Design:
 * designs/01-now/2026-08-06-reply-composer-width-and-progressive-disclosure-design.md §14.
 */
export function shouldKeyboardTuckHeader(win: Window, rootDocument: Document): boolean {
    const pathname = (win.location?.pathname ?? "").trim();
    const isChat = pathname === MESSAGES_PATHNAME;
    const isFocusedReplyComposer = pathname.startsWith(DISCUSSION_PATH_PREFIX)
        && rootDocument.activeElement?.closest?.(`[${REPLY_COMPOSER_ROOT_ATTRIBUTE}]`) != null;
    if (!isChat && !isFocusedReplyComposer) {
        return false;
    }

    return isSoftKeyboardOpen(win, rootDocument);
}

/**
 * D15 (owner, 2026-09-11) used to live here: on a PHONE, an open conversation tucked the site header so the
 * thread's own header was the only top bar. **Deleted 2026-10-05 with SV-157**, which removes the site header
 * from every phone route (A1): with no phone header there is nothing left for a thread to tuck, and the phone
 * reserve is already the hidden value D15 wrote. Stage 6 (`shouldKeyboardTuckHeader`) stays for 768 px and up.
 */
/**
 * `CREATE-PUBLISH-23` (wave-14 **Q29**): the composer matches the feeds rather than pinning the
 * header, so the writing screen stops spending ~19% of a 412px phone on chrome by inheritance.
 *
 * <p><b>The constraint Q29 binds on holds by construction:</b> the mobile Back affordance is rendered
 * by `DiscussionEditorWorkflowActions`' `top` variant, inside the composer's own workflow bar, not by
 * this header. Chrome that hides does not take the author's way out with it.
 *
 * <p><b>What this change does NOT do, stated so the next reader does not re-derive it.</b> The
 * composer's header was never pinned by its policy — the old `"lockVisible"` return was read by
 * nothing. Two other things keep it on screen and both survive Q29:
 * <ol>
 *   <li>`isLockedVisibleSurface` pins the header whenever `document.activeElement` is editable, and
 *       the composer body is a `contentEditable`. While the author types, the header stays.</li>
 *   <li>The mobile canvas is `height: calc(100dvh - 18rem - keyboardInset)` with its own
 *       `overflow-y-auto` (`DiscussionTextEditor.tsx`), so the DOCUMENT barely scrolls and
 *       `hideOnScroll` has little to act on.</li>
 * </ol>
 * Exempting the composer from (1) the way `/messages` is exempted, or giving the document the scroll
 * in (2), are separate decisions Q29 did not take. Parked in
 * `designs/07-archive/runs/2026-08-23-ux-critique-remediation-waves-10-and-11/WAVE-15-QUESTIONS/A2.3.md`.
 */
export function getMobileHeaderVisibilityPolicy(pathname: string): MobileHeaderVisibilityPolicy {
    const normalizedPathname = pathname.trim() || "/";

    if (normalizedPathname.startsWith("/create/discussion")) {
        return "hideOnScroll";
    }

    if (normalizedPathname === "/"
        || normalizedPathname === "/home"
        || normalizedPathname.startsWith("/search")
        || normalizedPathname.startsWith("/discussion")
        || normalizedPathname.startsWith("/group")
        || normalizedPathname.startsWith("/series")
        || normalizedPathname.startsWith("/user")) {
        return "hideOnScroll";
    }

    if (isStandaloneAccountAccessPath(normalizedPathname)
        || normalizedPathname === "/messages"
        || normalizedPathname.startsWith("/settings")
        || normalizedPathname.startsWith("/my_dashboard")
        || normalizedPathname.startsWith("/admin")
        || normalizedPathname.startsWith("/contact")) {
        return "visibleOnly";
    }

    return "visibleOnly";
}

/**
 * Whether `<main>` keeps the header's 4.5rem reserve, or hands it back.
 *
 * **This is keyed on the REASON the header is away, not on the fact that it is away** (2026-08-14).
 * The two callers want opposite things, and conflating them is what made the feed lurch:
 *
 *  - `"reserve"` — the scroll-driven tuck. The header is `fixed`, so this padding lives at the top of
 *    the DOCUMENT, not in the viewport: it is only ever on screen when scrolled to the top, and at
 *    `scrollTop <= MOBILE_HEADER_TOP_ZONE_PX` the header is forced visible anyway. Collapsing it
 *    therefore returns zero visible space, while pulling every card up by 72px mid-swipe, animating a
 *    layout property for 240ms on the longest document in the product, and — because browser scroll
 *    anchoring compensates for content resized above the viewport — feeding a phantom 72px scroll
 *    delta straight back into `syncVisibility`, which reads it as intent and reverses itself.
 *  - `"collapse"` — the chat keyboard tuck (Stage 6, 2026-08-12). Here the page is NOT scrolling and
 *    reclaiming the 72px is the entire point: with the keyboard up the thread is two bubbles tall. A
 *    header that vanished and left its gap behind was the half-fix that slice explicitly rejected.
 *
 * Design: `designs/01-now/2026-08-14-touch-scroll-fidelity-feed-menus-and-header-reserve-design.md`.
 */
type MainContentPaddingMode = "reserve" | "collapse";

function updateMainContentPadding(mode: MainContentPaddingMode): void {
    if (typeof document === "undefined") {
        return;
    }

    const mainContent = document.getElementById("seavien-main-content");
    if (!mainContent) {
        return;
    }

    if (isPhoneViewportNow()) {
        // SV-157 A3: no phone header, so nothing to reserve, tuck or tween. A viewport-contract template declares
        // the same phone value itself (BL-140's reason below still holds: an inline write would outrank it), and
        // every other page gets it inline, so a template that still carries an old reserve cannot leave a gap.
        mainContent.style.transition = "none";
        if (templateOwnsHeaderReserve(mainContent)) {
            mainContent.style.removeProperty("padding-top");
            return;
        }
        mainContent.style.paddingTop = PHONE_TOP_RESERVE_PADDING_TOP;
        return;
    }

    // Under reduced-motion the header still tucks/reveals — it just snaps instead of easing (WCAG
    // 2.3.3). Behavior (padding value) is unchanged; only the transition tween is removed. The
    // declaration stays even though the scroll path no longer changes the value, because the keyboard
    // tuck still eases between the two.
    mainContent.style.transition = prefersReducedMotion()
        ? "none"
        : `padding-top ${MOBILE_HEADER_TRANSITION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
    if (mode === "reserve" && templateOwnsHeaderReserve(mainContent)) {
        // BL-140 (2026-09-15): on a viewport-contract surface (`/messages`, the editor) the TEMPLATE
        // declares the reserve — `messages.jte` says `4rem` below `sm` and `4.5rem` from `sm`, because
        // the mobile header is `4rem` tall — and the flat inline write below over-padded the phone by
        // 8px, leaving a dead strip between the header and the chat panel. An inline style outranks
        // every stylesheet rule, so the only way to let the template's number apply is to not write one.
        // The keyboard tuck ("collapse") still writes its hidden value inline; the reserve returns by
        // removal, which the 240ms tween above animates the same way.
        mainContent.style.removeProperty("padding-top");
        return;
    }
    mainContent.style.paddingTop = mode === "reserve" ? MOBILE_HEADER_VISIBLE_PADDING_TOP : MOBILE_HEADER_HIDDEN_PADDING_TOP;
}

/**
 * BL-140: does `<main>` carry a `data-viewport-contract`? `basic_layout.jte` renders the attribute for
 * every page, EMPTY when the template passed none, so presence is not the test — a non-empty value is.
 * `EnhancedNavigation.mountClientOwnedRoute` writes and removes it on the in-app path.
 */
function templateOwnsHeaderReserve(mainContent: HTMLElement): boolean {
    return (mainContent.getAttribute(VIEWPORT_CONTRACT_ATTRIBUTE) ?? "").trim() !== "";
}

function getMobileHeaderVisibilityStyle(isVisible: boolean): React.CSSProperties {
    return {
        height: "calc(4rem + env(safe-area-inset-top))",
        paddingTop: "calc(0.5rem + env(safe-area-inset-top))",
        borderBottomWidth: "1px",
        transform: isVisible ? MOBILE_HEADER_VISIBLE_TRANSLATE_Y : MOBILE_HEADER_HIDDEN_TRANSLATE_Y,
        pointerEvents: isVisible ? "auto" : "none",
        // Reduced-motion: snap the slide-away transform (0ms) rather than easing it. The header
        // still hides/reveals on scroll; the 240ms tween is what's removed.
        transitionDuration: prefersReducedMotion() ? "0ms" : `${MOBILE_HEADER_TRANSITION_DURATION_MS}ms`,
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    };
}

/**
 * The post composer, in all three of the shapes the client route registry matches:
 * `/create/discussion`, `/create/discussion/new` and `/create/discussion/{draftId}`.
 *
 * <p>Written here rather than imported from `navigation/composerRoutes.ts` for the same reason that
 * module exists at all: it is dependency-free on purpose, and this file already string-matches the
 * composer prefix a few lines up in `getMobileHeaderVisibilityPolicy`. If a fourth composer entry
 * path is ever added, add it in `composerRoutes.ts` and make both readers consult it.
 *
 * <p>Deliberately `startsWith("/create/discussion/")` and not `startsWith("/create/discussion")`:
 * the latter would also claim a future `/create/discussions` sibling.
 */
function isOnComposerPath(pathname: string): boolean {
    return pathname === CREATE_DISCUSSION_PATH || pathname.startsWith(`${CREATE_DISCUSSION_PATH}/`);
}

/**
 * The email-entry `openLogin=1` prompt. It only runs for a LOGGED-OUT visitor (its effect bails when logged in),
 * and r50 D11 (FI-994, X-54) honours `loginUsername` only for a session that already is the email delivery's
 * recipient, which a logged-out visitor never is: a member who pasted their email CTA into a chat showed their
 * username to every logged-out reader. So the param is still stripped from the address bar, but never returned
 * as a prefill. The server login wall drops it the same way (`WebSecurityConfig.buildLoginEntryPointUrl`).
 */
function readEmailLoginPrompt(): { cleanedUrl: string } | null {
    if (typeof window === "undefined") {
        return null;
    }

    const url = new URL(window.location.href);
    const openLogin = url.searchParams.get("openLogin")?.trim();
    if (openLogin !== "1") {
        return null;
    }

    url.searchParams.delete("openLogin");
    url.searchParams.delete("loginUsername");

    return {
        cleanedUrl: `${url.pathname}${url.search}${url.hash}`,
    };
}

function readEmailSignupPrompt(): { email: string | null; cleanedUrl: string } | null {
    if (typeof window === "undefined") {
        return null;
    }

    const url = new URL(window.location.href);
    const openSignup = url.searchParams.get("openSignup")?.trim();
    if (openSignup !== "1") {
        return null;
    }

    const signupEmail = url.searchParams.get("signupEmail")?.trim() ?? "";
    // Strip only the one-shot signup-prompt params; keep attribution params (lead, emsrc, ...).
    url.searchParams.delete("openSignup");
    url.searchParams.delete("signupEmail");

    return {
        email: signupEmail.length > 0 ? signupEmail : null,
        cleanedUrl: `${url.pathname}${url.search}${url.hash}`,
    };
}

type LazyMessagesDrawerProps = {
    localizer: Localizer;
    onReady?: () => void;
};

/**
 * The chat feature, off the startup path.
 *
 * <p>This was `React.lazy(async () => require(...))` until 2026-08-01 — which defers the *mount* and
 * splits *nothing*. esbuild decides eager-vs-lazy from the edge kind: `import()` is a dynamic edge and
 * gets its own chunk, `require()` is a static edge and is inlined into the importer's output. So
 * `MessagesDrawer` → `ChatExperience` → the react-query core sat in the eager startup graph of every
 * page, including anonymous landings that can never open a DM, while reading exactly like a code
 * split. `scripts/check-bundle-budget.mjs` walks that same edge distinction, which is why it — and
 * not the source — is the oracle for this.
 *
 * <p>Routed through `lazyWithRetry` per AGENTS.md rule 26: a real `import()` can now fail over the
 * network (a blip, or a stale `main_bundle.js` referencing a chunk hash the origin no longer serves),
 * `React.lazy` caches that rejection permanently, and `MountErrorBoundary` does not cover it. The
 * `<React.Suspense>` fallback below is load-bearing for the same reason — it renders the ordinary
 * messages button with the live unread badge, so the header stays complete and clickable while the
 * chunk is in flight.
 *
 * <p>FI-2912 (r156): this drawer is a ROOT of the chat catalog surface (`esbuild.shared.cjs` `DEFERRED_SURFACES`), so
 * the chat-only keys live in `messages_<locale>_deferred_chat.js` and the importer waits for it (and for the general
 * deferred half) before the drawer can paint; the same fallback covers that wait. The idle and hover warm-ups warm both
 * through `warmMessagesDrawer` below, never `preload()` (FI-4518).
 */
const LazyMessagesDrawer = lazyWithRetry<React.ComponentType<LazyMessagesDrawerProps>>(
    () => import("@/main/react/header/sidebar/chat/MessagesDrawer")
        .then((module) => ensureSurfaceCatalogLoaded("chat").then(() => ({
            default: module.MessagesDrawer as React.ComponentType<LazyMessagesDrawerProps>,
        }))),
    "messages-drawer",
);

/**
 * FI-4518 (r169 r5): the chat WARM-UP (idle and hover), deliberately NOT `LazyMessagesDrawer.preload()`. A preload runs
 * the lazy importer through the RELOADING helper and pins the lazy component to whatever that run produced, so a
 * warm-up the member never asked for could reload the page under a half-written composer, and a surface catalog that
 * failed during it left the first real open with raw keys and no retry. This fetches the same chunk RETRY-ONLY and
 * chains the same surface wait; nothing in the tree changes (rule 26), the module map keeps the chunk, and the first
 * real open re-runs the lazy importer, whose surface wait retries a failed load (`ensureSurfaceCatalogLoaded`).
 */
function warmMessagesDrawer(): void {
    void importChunkWithRetry(
        () => import("@/main/react/header/sidebar/chat/MessagesDrawer")
            .then((module) => ensureSurfaceCatalogLoaded("chat").then(() => ({default: module}))),
        "messages-drawer-warm",
    ).catch(() => undefined);
}

type LazyNotificationsDrawerProps = {
    localizer: Localizer;
    openRequest?: NotificationsDrawerOpenRequest | null;
};

/**
 * The notifications feature, off the startup path (2026-08-30, wave-2 of the performance plan).
 *
 * <p>`MainHeader` mounts on every page, and this one static import was the single edge by which it
 * statically reached 93.2% of the application. Cutting it drops **65 modules / 581.5 KB** off the
 * eager graph: `NotificationsView` itself (53.9 KB), and then everything only it reached —
 * `NotificationItem` -> `GroupDiscussionSubmissionReviewDialog` -> `DiscussionCardDisplayer` ->
 * `DiscussionCardMediaCarousel` -> `components/ui/carousel` -> `embla-carousel` (47 KB of carousel
 * library that every anonymous landing page was parsing), plus `vaul`, `cmdk`, `DiscussionButtons`,
 * `SubjectCombobox`, `SensitiveMediaGuard` and `AddDiscussionToSeriesDialog`.
 *
 * <p>Routed through `lazyWithRetry` per AGENTS.md rule 26, exactly like `LazyMessagesDrawer` above and
 * for the same reasons: a real `import()` can fail over the network (a blip, or a stale
 * `main_bundle.js` referencing a chunk hash the origin no longer serves), `React.lazy` caches that
 * rejection permanently, and `MountErrorBoundary` does not cover a rejection raised outside React's
 * render tree. The `<React.Suspense>` fallback is load-bearing for the same reason — it renders the
 * ordinary bell with the live badge, dot and pending-decisions ring, so the header stays complete and
 * clickable for the whole in-flight window and JS never subtracts from what was there (rule 32).
 *
 * <p><b>This defers the DOWNLOAD, not the feature.</b> The chunk is PRELOADED on hover, focus or touch
 * of the bell and otherwise on the browser's next idle callback, so a logged-in member has it in cache
 * a moment after load. `notificationsFeatureMounted` (the MOUNT) arms only on a click of the bell or an
 * inbound `openRequest`: mounting swaps the bell's DOM node, and a swap the member did not ask for
 * swallows the click it lands under (2026-08-30 for hover, 2026-09-21 for idle).
 *
 * <p>The deferred-catalog wait (`NOTIFICATIONS-61`, A7): the drawer's own strings live in the DEFERRED
 * catalog half, and this chunk is not mounted through `ReactMounter`, so it waits for that half here;
 * the ordinary-bell fallback covers the wait instead of a drawer painting dotted keys.
 */
const LazyNotificationsDrawer = lazyWithRetry<React.ComponentType<LazyNotificationsDrawerProps>>(
    () => import("@/main/react/header/sidebar/notification/NotificationsView")
        .then((module) => ensureDeferredCatalogLoaded().then(() => ({
            default: module.NotificationsDrawer as React.ComponentType<LazyNotificationsDrawerProps>,
        }))),
    "notifications-drawer",
);

/** r53 D11: where the phone header bell goes on a route where the bottom nav stands down. */
const PHONE_NOTIFICATIONS_PAGE_PATH = "/notifications";
/** SV-169 `1b`: the Conversations destination (`ConversationsPageController`, backend slice `1a`). */
export const CONVERSATIONS_PAGE_PATH = "/conversations";

/**
 * SV-149 A3: where a phone sends `?openNotifications=1` now that the drawer is retired there, and (A11, settle
 * round 57) where desktop sends one that names no section.
 *
 * <p>The continuation's section becomes the page's scope and its read filter the page's filter, in the
 * page's own address shape (`buildNotificationsUrl` in `NotificationsPage.tsx`, the drawer footer link
 * in `NotificationsView.tsx`): no section is the combined list at the bare `/notifications`, and "all"
 * is the page's default so it is absent from the address. Spelled out here rather than imported because
 * `NotificationsPage` is a lazy page chunk and this file is in the eager startup graph.
 */
export function buildPhoneNotificationsPageUrl(prompt: Pick<ParsedNotificationContinuationPrompt, "section" | "readFilter">): string {
    const pathname = prompt.section ? `/notifications/${encodeURIComponent(prompt.section)}` : "/notifications";
    return prompt.readFilter === "unread" ? `${pathname}?filter=unread` : pathname;
}

type MainHeaderProps = {
    loggedIn?: boolean;
    profileImageUrl?: string | null;
    username?: string | null;
    searchQuery?: string | null;
    searchType?: string | null;
};

/**
 * What is left of the anonymous header's account-access entry points after operator decision D5
 * (`LANDING-54`, `ACCOUNT-ACCESS-3`, 2026-08-26).
 *
 * <p>It used to be four. Three of them - create, messages, notifications - were unlabelled member
 * chrome that an anonymous visitor cannot use, and **all three opened the same dialog**, so a 412px
 * phone spent three of its five header controls on one destination and had no room left for the one
 * control that says the word "Log In". D5 collapsed them into the labelled pill.
 *
 * <p>`"messages"` survives with **no control behind it**: it is reached only through the programmatic
 * `onOpenChatDrawer` path, which is how a message intent raised elsewhere in the app (a profile's
 * "Message" button, a deep link) reaches the login continuation. Deleting it would silently drop that
 * continuation, which is a different feature from the header glyph this decision removed.
 */
type AnonymousHeaderAccountAccessAction = "messages" | "sign-in";

export function MainHeader({
                        loggedIn = false,
                        profileImageUrl = null,
                        username = null,
                        searchQuery = "",
                        searchType = "discussions",
                        localizer,
                    }: MainHeaderProps & { localizer: Localizer }) {
    const headerInbox = useHeaderInbox();
    const shellStateSnapshot = React.useSyncExternalStore(
        subscribeToShellState,
        getShellStateSnapshot,
        getShellStateSnapshot,
    );
    const shellState = shellStateSnapshot.mainHeader;
    const effectiveLoggedIn = shellState?.loggedIn ?? loggedIn;
    /*
     * The badge counts unread DMs plus unread group rooms the member TAKES PART IN.
     *
     * From 2026-08-19 to 2026-08-22 this drew `directUnreadCount` and gave group rooms a dot. The
     * reason was sound and is still on the record: a summed badge read `90` every day, almost all of
     * it rooms the member had been added to and never opened, so the number was never actionable and
     * trained them to ignore the DM with it. What the dot could not do was sustain a conversation.
     * Room traffic fell 85% on the first full evening after that shipped, members kept opening the
     * rooms and finding them dead, and two regulars moved 605 messages a day into DMs with each other
     * — the one surface that still carried a number.
     *
     * The fix is not a revert: the server now counts only rooms the member has read before or posted
     * in, which drops the group half from a median of 13 to 2 on real data while leaving the people
     * who actually talk in rooms untouched. So the total is once again a number that means something,
     * and it is the total that belongs on the chrome. See
     * designs/01-now/2026-08-19-header-badges-count-what-is-addressed-design.md §8.
     *
     * No new rule-31 exposure: `messages.unreadCount` was already in the observed snapshot and already
     * moved on every group message. This stops discarding a number we already compute and diff.
     *
     * WHICH fields those are is `messagesBadge`'s to decide, not this file's: `MessagesDrawer` renders
     * this same button once the lazy chat chunk arrives, and on 2026-08-22 the amendment above changed
     * only this side — so the total rendered on load and a dot replaced it a moment later (design §9).
     * Read the source here, ask the selector what to draw.
     */
    const messagesBadgeSource = effectiveLoggedIn
        ? (headerInbox.loggedIn ? headerInbox.data.messages : shellStateSnapshot.headerInbox?.messages)
        : null;
    // No group dot (round 12 A4): the count already includes every unread room.
    const { count: effectiveMessagesUnreadCount } = messagesBadge(messagesBadgeSource);
    /*
     * The bell's three derivations, read HERE as well as inside the drawer so the pre-chunk bell shows
     * the same badge as the post-chunk one. All three are pure functions of the store snapshot — rule
     * 31: nothing is stored, so a poll tick that changes nothing changes no identity, and there is no
     * second copy of the fact for two effects to swap.
     */
    const notificationsActionableCount = actionableNotificationCount(headerInbox.data.notifications);
    const notificationsHasPassiveUnread = hasPassiveUnreadNotifications(headerInbox.data.notifications);
    const notificationsPendingDecisions = hasPendingDecisions(headerInbox.data.notifications);
    // SV-182 (D1, A1, A4): the Conversations button's number and dot, the server's card count (the hub's All tab draws
    // the same value, SV-185); the SV-180 row derivation only while the server did not count. Destructured to primitives so
    // the memoized controls below depend on values, not a fresh object (rule 31).
    const {
        count: conversationsUnreadCount,
        showActivityDot: conversationsHasPassiveUnread,
    } = conversationsNotificationBadge(headerInbox.data.notifications, headerInbox.data.conversationsBadge);
    const effectiveProfileImageUrl = shellState?.profileImageUrl ?? profileImageUrl ?? null;
    const effectiveUsername = shellState?.username ?? username ?? null;
    const effectiveSearchQuery = shellState?.searchQuery ?? searchQuery ?? "";
    const effectiveSearchType = shellState?.searchType ?? searchType ?? "discussions";
    // Default to verified so we never show the confirm-email nudge until the shell state explicitly
    // tells us the logged-in member's email is unconfirmed.
    const [profileImageUrlState, setProfileImageUrlState] = React.useState<string | null>(effectiveProfileImageUrl);
    const [chatFeatureMounted, setChatFeatureMounted] = React.useState(false);
    const [chatDrawerReady, setChatDrawerReady] = React.useState(false);
    const [pendingChatOpenPayload, setPendingChatOpenPayload] = React.useState<OpenChatDrawerPayload | null>(null);
    const [notificationOpenRequest, setNotificationOpenRequest] = React.useState<NotificationsDrawerOpenRequest | null>(null);
    /*
     * Has the notifications CHUNK been asked for yet? False on first paint of every page, which is the
     * point: the drawer is `LazyNotificationsDrawer` and this gate is what keeps its 581.5 KB closure
     * out of the startup graph. Armed ONLY by a click on the bell or an inbound `openRequest`; hover,
     * focus, touch and the idle callback below PRELOAD the chunk and never mount it. Same ladder as
     * `chatFeatureMounted` for the chat drawer.
     */
    const [notificationsFeatureMounted, setNotificationsFeatureMounted] = React.useState(false);
    // Seed null (never show the banner synchronously). `navigator.onLine === false` is only a
    // hint — DevTools throttling, request-intercepting extensions, and a known stuck-Chrome
    // notifier all report offline while the server is perfectly reachable. The connection effect
    // below verifies with a real reachability probe before ever surfacing the banner.
    const [connectionState, setConnectionState] = React.useState<"offline" | "reconnected" | null>(null);

    /*
     * Tier-2 finding 28 — the header search input, which had no ref at all until `/` needed to focus it.
     * (Named "desktop" from when a second, phone search view existed; FI-1342 removed it.)
     */
    const desktopSearchInputRef = React.useRef<HTMLInputElement>(null);
    const topBannerStackRef = React.useRef<HTMLDivElement | null>(null);
    const reconnectTimeoutRef = React.useRef<number | null>(null);
    const emailLoginPromptHandledRef = React.useRef(false);
    const emailSignupPromptHandledRef = React.useRef(false);
    const notificationPromptHandledRef = React.useRef(false);
    const useDedicatedMessagesPage = useDedicatedMessagesPagePreference();
    const [mobileHeaderVisible, setMobileHeaderVisible] = React.useState(true);
    const mobileHeaderStateRef = React.useRef<{
        visible: boolean;
        lastScrollTop: number;
        scrollAnchorTop: number;
        lastDirection: "up" | "down" | null;
    }>({
        visible: true,
        lastScrollTop: 0,
        scrollAnchorTop: 0,
        lastDirection: null,
    });

    const searchLabel = localizer("header.tooltip.search");
    // S7 U13 (`DISCOVERY-119`, `EMPTY-STATES-140`): the header field names its scope when the page has
    // pre-scoped it. `/groups` passes `searchType = "groups"` (groups_directory.jte), so the header reads
    // "Search groups…" there — the directory's own existing key, already in all 25 catalogs — instead of
    // a bare "Search..." beside a field that says "Search groups…". Every other scope keeps the generic.
    const searchPlaceholder = effectiveSearchType === "groups"
        ? localizer("groups.directory.search_placeholder")
        : `${searchLabel}...`;
    const menuLabel = localizer("header.tooltip.menu");
    const baseCreateLabel = localizer("header.tooltip.create");
    // `CREATE-PUBLISH-10` / D7. Reused from the drafts dialog's own new-draft button, already in all
    // 25 catalogs, so the header and the dialog name the same action with the same words.
    const startAnotherDraftLabel = localizer("create.drafts-list.new-draft-button");
    const messagesLabel = localizer("header.tooltip.messages");
    const conversationsLabel = localizer("header.tooltip.conversations");
    const notificationsLabel = localizer("header.tooltip.notifications");
    // `PROFILE-23` (S7 C34): the "needs a decision" dot is colour-only, so its text equivalent rides the
    // bell's accessible name. The name still STARTS with the visible tooltip's noun (WCAG 2.5.3); the
    // tooltip itself stays the plain label.
    const notificationsAccessibleName = notificationsPendingDecisions
        ? localizer("header.tooltip.notifications.pending_decisions")
        : notificationsLabel;
    const mainSideBarProps: { localizer: Localizer } & MainSideBarViewProps = {
        localizer,
        loggedIn: effectiveLoggedIn,
    };

    const hasConnectionBanner = connectionState !== null;
    // The verify-email nudge is a floating bottom-right card now (see the 2026-06-10 design rework);
    // it no longer joins the top banner stack and never pushes header/content down.
    const hasTopBannerStack = hasConnectionBanner;

    /*
     * `MESSAGES-2` — the header's own messages control, before the chat chunk has loaded.
     *
     * `MessagesDrawer` carries the same check, but it is lazy: on a cold `/messages` load the button
     * on screen is the one rendered a few hundred lines below, and this is the callback behind it. A
     * check that lived only in the drawer would therefore be absent in the exact case the register
     * measured — pressing the glyph on arrival.
     *
     * On this route the request is neither a navigation nor a mount. Both would be dead ends: the
     * mobile branch would re-navigate to the page it is on, discarding scroll and any composer draft,
     * and the desktop branch would pull down a second copy of the chat feature to lay a drawer over
     * the page already rendering it.
     */
    const currentPathname = useCurrentPathname();
    const isOnMessagesPage = isOnMessagesPagePath(currentPathname);
    /*
     * Bottom navigation inputs (slice `1d`). Two hooks the header did not hold before, both rule-31
     * shaped: `useMediaQueryMatches` publishes a boolean over the phone query (unchanged query, no
     * re-render), and `useKeyboardInsetPx` publishes a number that moves only when the keyboard does.
     */
    const isPhoneViewport = useMediaQueryMatches(MOBILE_VIEWPORT_QUERY_768);
    const keyboardInsetPx = useKeyboardInsetPx();
    const mobileBottomNavVisible = getMobileBottomNavVisibility(currentPathname, keyboardInsetPx);
    /**
     * SV-149 R1 + A2 (2026-10-01): **on a phone the bottom nav owns the doors, so the top bar slims.**
     *
     * <p>⚠️ Superseded on a phone by SV-157 A1 (`headerRendered` below): the phone header does not render at all
     * now, except as the transient search bar, which is the only thing this predicate still shapes there.
     *
     * <p>"Phone" is the bar's OWN visibility predicate and nothing new: the phone query plus
     * `getMobileBottomNavVisibility` on this route. Then the header renders no Search, Create, Messages
     * or Bell (the bar has all four), the notifications drawer is unreachable (the page is the phone's
     * one notifications surface), and `?openNotifications=1` is redirected to that page. Wherever the
     * bar stands down BY ROUTE (`/messages*`, `/discussion/*`, `/create/*`, account-access routes,
     * unsubscribe landings) this is false and the header keeps every control, so no phone route is ever
     * left without a door.
     *
     * <p>The predicate's KEYBOARD term is deliberately neutralised (inset 0) here: the bar steps aside
     * for a soft keyboard, but the header is NOT tucked for one on ordinary routes
     * (`shouldKeyboardTuckHeader` covers chat and a focused reply composer only), so following the
     * keyboard would grow four controls into a visible header every time a member typed into a form.
     *
     * <p>Removed by MOUNT, never by a class: an unmounted bell cannot mount the lazy drawer, and a
     * `hidden` one would still be in the accessibility tree's reach of every spec and scanner.
     */
    const bottomNavOwnsTheDoors = isPhoneViewport && getMobileBottomNavVisibility(currentPathname, 0);
    /**
     * SV-157 A1 (2026-10-05): **on a phone the site header does not render at all**, on every route, for members
     * and visitors alike (D3). Removed by MOUNT, like `bottomNavOwnsTheDoors` above and for its reason: a control
     * that is not in the tree cannot be reached by a spec, a scanner or a screen reader, and cannot mount a drawer.
     * The component itself stays mounted on a phone, because it owns more than the bar: the sidebar provider, the
     * bottom navigation, the connection banner and the one-shot email and notification prompts.
     *
     * <p>No exception since round 67 D2 (slice `1g`, 2026-10-05): the transient phone search bar the bottom bar's
     * Search tab used to mount here (`|| isSearchMode`) is gone. The tab is a link to `/search`, whose own field
     * carries the header's suggestions (`HeaderSearchBar` with `searchPageField`).
     */
    const headerRendered = !isPhoneViewport;
    /**
     * r53 D11 (2026-10-02; closes SV-149 R1 on bar-down routes): **on a phone the notifications drawer is not
     * reachable at all.** Where the bar shows, the header has no bell (above). Where it stands down BY ROUTE
     * (`/discussion/*`, `/messages*`, `/create/*`) the header bell is a plain link to the page, so nothing mounts
     * the drawer, and `?openNotifications=1` is answered by the page on every phone route. Desktop and tablet
     * keep the drawer (A9). The phone query alone, for the same reason `bottomNavOwnsTheDoors` uses it.
     */
    const phoneNotificationsArePageOnly = isPhoneViewport;
    /**
     * `ACCOUNT-ACCESS-3`, operator decision `A` (2026-08-20, applied wave 8 batch J): **the chrome's
     * account-access entry points do not present themselves on a standalone account-access route.**
     *
     * <p>The FAB half shipped in wave 6 (`FloatingActionConfig.ts`, a rule matching this same route
     * class and resolving `actions: []`). The three HEADER entries were gated here in wave 9, and
     * **deleted outright in wave 16 under D5** — see the block above `anonSignInControl`. So this
     * predicate no longer gates them, and what it guards today is exactly one thing: whether the
     * labelled sign-in pill takes its new MOBILE appearance. On a standalone account-access route it
     * does not, because that pill is this row's surviving remainder and it is parked on the
     * `ACCOUNT-ACCESS-17` coupling rather than free to change.
     *
     * <p>The predicate is `isStandaloneAccountAccessPath`, which this file has enumerated and consulted
     * since long before the row — originally for AUTO-opened dialogs only.
     *
     * <p>Read through `useCurrentPathname` (a `useSyncExternalStore` over the soft-nav intent event and
     * `popstate`) rather than `window.location` so a soft navigation onto or off one of these routes
     * re-resolves it, exactly as the FAB does.
     */
    const onStandaloneAccountAccessPath = isStandaloneAccountAccessPath(currentPathname);
    /**
     * `CREATE-PUBLISH-10`, operator decision D7 (2026-08-26): **on the composer, the compose control
     * means "start another".**
     *
     * <p>It was a plain `<a href="/create/discussion">` with no route awareness, so what it did
     * depended entirely on the draft you were sitting in. On an empty draft `reuseEmptyOrCreate`
     * returned *the draft already on screen* and nothing visible happened - the member pressed the one
     * control that says "write", twice, and concluded the app was broken. On a draft with content it
     * silently swapped the author onto a different document. Meanwhile `/create/discussion/new`, the
     * route that genuinely starts a second draft, was linked from no chrome control anywhere.
     *
     * <p>Read through `useCurrentPathname` for the same reason `isOnMessagesPage` is: a soft
     * navigation into or out of the composer has to re-resolve it, and a `useState` mirror of an
     * externally-owned value renders once with the wrong one inside this island (rule 31).
     */
    const onComposerPath = isOnComposerPath(currentPathname);

    /*
     * Stable by identity because it crosses `MessagesDrawer`'s memo boundary.
     *
     * It was `onReady={() => setChatDrawerReady(true)}` written inline in the JSX until 2026-08-22,
     * which is a changed prop on EVERY render of this component — so every scroll tick, every store
     * notify and every viewport change re-rendered the lazy chat chunk, vaul's `Root`, `DialogPortal`
     * and both `Presence` layers under it, recomposing the drawer's callback refs each time. See the
     * memoized elements below and the design for the arithmetic.
     */
    const handleChatDrawerReady = React.useCallback(() => setChatDrawerReady(true), []);

    const requestChatDrawerOpen = React.useCallback((payload: OpenChatDrawerPayload = {}) => {
        if (isOnMessagesPage) {
            // MESSAGES-166 (FI-5089): the current door re-taps; the inbox field only while no page answers.
            if (!namesAConversation(payload) && retapBottomNavTab("messages")) {
                return;
            }
            focusMessagesPageInbox();
            return;
        }

        if (useDedicatedMessagesPage) {
            navigateToUrl(buildMessagesUrl(payload), {source: "header-mobile-messages"});
            return;
        }

        setPendingChatOpenPayload(payload);
        setChatFeatureMounted(true);
    }, [isOnMessagesPage, useDedicatedMessagesPage]);

    /**
     * Hover / focus / touch on the messages button: pull the chat chunk down and nothing else.
     *
     * <p>⚠️ Same obligation as `armNotificationsFeature` below, for the same reason and on the same
     * evidence: this used to call `setChatFeatureMounted(true)`, which replaces the idle messages
     * button with a `<Suspense>` subtree — a remount, under the member's pointer, between `mousedown`
     * and `mouseup`. <b>Warming the chunk must not change the rendered tree.</b> The mount happens on
     * the click itself (and on an `onOpenChatDrawer` request); since 2026-09-21 the idle rung below
     * only preloads too, for the same reason.
     */
    const requestDesktopChatFeatureMount = React.useCallback(() => {
        if (isOnMessagesPage || useDedicatedMessagesPage) {
            return;
        }

        warmMessagesDrawer();
    }, [isOnMessagesPage, useDedicatedMessagesPage]);

    /**
     * Hover / focus / touch on the bell: pull the chunk down AND warm the preview.
     *
     * <p>The preview half is `NOTIFICATIONS-28` and is unchanged in effect — the drawer's own bell
     * did exactly this, and the store de-duplicates, so the open path still reuses whatever this
     * started rather than reissuing it. The chunk half is what makes the fallback window short: by the
     * time a member's pointer has crossed the button, the chunk request is already out.
     *
     * <p>⚠️ <b>THIS MUST NOT CHANGE THE RENDERED TREE, and that is the whole point of `preload()`.</b>
     * It used to call `setNotificationsFeatureMounted(true)` here, which swapped the idle bell for
     * `<Suspense fallback={notificationsTooltipPending}>` — a different element TYPE at the same
     * position, so React unmounted the button and mounted a new one. A pointer press straddles that
     * swap: the browser delivered `mousedown` to the old node and `mouseup` to its replacement, and
     * because the two have no common connected ancestor <b>it fired no `click` event at all</b>. The
     * member's first bell click was swallowed in silence; the second worked. Measured against devo on
     * 2026-08-30 (event log: `mouseover, mousedown, mouseover, mouseup` — no `click`), and it is the
     * defect that rejected the `6389739721` and `79aec899b3` gates. The same reasoning binds the
     * keyboard and touch paths, which arm through this same callback: a focus-armed swap destroys the
     * focused element before Enter can reach it.
     *
     * <p>Stable by identity (`[]`), which is load-bearing: it is a dependency of the memoized bell
     * elements below, and an inline handler there re-arms the tooltip on every header render and
     * silently restores the React #185 ref-churn amplifier (rule 31).
     */
    const armNotificationsFeature = React.useCallback(() => {
        LazyNotificationsDrawer.preload();
        void refreshHeaderInbox({ mode: "preview", reason: "notifications-prefetch" });
    }, []);

    /**
     * Click on the bell before the drawer exists: arm the mount and leave a request behind for it.
     *
     * <p>`NotificationsDrawer` opens itself from `openRequest`, so the click is not lost while the
     * chunk is in flight — the drawer opens the moment it mounts. That only works because the drawer
     * seeds `lastHandledOpenRequestKeyRef` to 0 rather than to the incoming key; see the ⚠️ there.
     */
    const requestNotificationsDrawerOpen = React.useCallback(() => {
        setNotificationsFeatureMounted(true);
        setNotificationOpenRequest((previous) => ({ key: (previous?.key ?? 0) + 1 }));
    }, []);

    const openAnonymousHeaderAccountAccess = React.useCallback((
        action: AnonymousHeaderAccountAccessAction,
        payload: OpenChatDrawerPayload = {},
    ) => {
        if (action === "sign-in") {
            // LANDING-17 / wave-8 decision D9(a). The one path an anonymous visitor has no label for
            // is the one a returning member needs: search traffic includes people who already have an
            // account, and a member who has been silently logged out cannot tell that they are.
            // Opens on the LOGIN tab, not signup, and continues back to the page they were reading -
            // this is orientation, not acquisition. The absence of a promotional header CTA stays
            // deliberate (designs/04-live/2026-05-20-anonymous-header-quiet-account-gating-design.md).
            openAccountAccessDialog(localizer, {
                surface: "header_sign_in",
                contextTitle: localizer("account-access.context.account.title"),
                // Reused, not minted (wave-8 §3 puts this row at 0-1 keys and prefers reuse): the
                // account-context pair is the generic "you are at the account door" copy and is
                // already translated in all 25 locales. The label above reuses the dialog's own login
                // key, so the control and the thing it opens say the same word.
                contextDescription: localizer("account-access.context.account.message"),
                initialTab: "login",
                continuation: {
                    kind: "url",
                    url: getCurrentRelativeUrl("/home"),
                },
            });
            return;
        }

        // `"messages"`. No header control reaches this any more (D5 deleted the glyph); the caller is
        // the programmatic `onOpenChatDrawer` listener below, so an anonymous "message this person"
        // raised anywhere in the app still lands on the login form carrying its intent.
        openAccountAccessDialog(localizer, {
            surface: "header_messages",
            contextTitle: localizer("account-access.context.messages.title"),
            contextDescription: localizer("account-access.context.messages.message"),
            continuation: {
                kind: "messages",
                payload,
            },
        });
    }, [localizer]);

    React.useEffect(() => {
        setProfileImageUrlState(effectiveProfileImageUrl);
    }, [effectiveProfileImageUrl]);

    React.useEffect(() => {
        return onProfileUpdated((payload) => {
            if (payload?.profileImageUrl !== undefined) {
                setProfileImageUrlState(payload.profileImageUrl ?? null);
            }
        });
    }, []);

    /*
     * Warm the chat chunk once the page is idle: PRELOAD ONLY, never mount.
     *
     * <p>⚠️ Same obligation as `armNotificationsFeature`, and the idle rung is not exempt from it. This
     * used to `setChatFeatureMounted(true)`, which swaps the idle button for the Suspense fallback and
     * then for the drawer's own button on the PAGE's schedule. A press that straddles either swap fires
     * no `click` (measured on devo 2026-09-21 for the bell: 6 of 50 ordinary presses lost in the first
     * 300 ms). The mount now happens only on a request (`requestChatDrawerOpen`, which queues the
     * payload first), so any swap comes after the click has already been dispatched.
     * designs/08-audits-and-process/2026-09-21-devo-gate-triage-d83b637e80.md
     */
    React.useEffect(() => {
        if (!effectiveLoggedIn || chatFeatureMounted || typeof window === "undefined" || useDedicatedMessagesPage) {
            return;
        }

        const warmChunk = () => warmMessagesDrawer();
        const idleCallback = (window as Window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
            cancelIdleCallback?: (id: number) => void;
        }).requestIdleCallback;

        if (typeof idleCallback === "function") {
            const idleId = idleCallback(warmChunk, { timeout: 1200 });
            return () => {
                (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleId);
            };
        }

        const timeoutId = window.setTimeout(warmChunk, 500);
        return () => window.clearTimeout(timeoutId);
    }, [chatFeatureMounted, effectiveLoggedIn, useDedicatedMessagesPage]);

    /*
     * Warm the notifications chunk once the page is idle: PRELOAD ONLY, never mount.
     *
     * <p>Same shape and the same reasoning as the chat warm-up directly above: the split is about
     * STARTUP JS, not about withholding the feature. A logged-in member has the chunk in cache a beat
     * after load, off the critical path. Anonymous visitors never warm it at all (they have no bell),
     * which is where the whole win lands.
     *
     * <p>⚠️ It MOUNTED here until 2026-09-21, which was the hover defect again on a timer: the mount
     * swaps the idle bell for the Suspense fallback and then for the drawer's bell, and a press
     * straddling either swap fires no `click`. It failed two stateful specs at the `d83b637e80` gate and
     * reproduced on devo in 6 of 50 ordinary presses. The mount is now driven only by
     * `requestNotificationsDrawerOpen` and the deep-link prompt, both of which leave an `openRequest`
     * for the drawer to open from.
     */
    React.useEffect(() => {
        if (!effectiveLoggedIn || notificationsFeatureMounted || typeof window === "undefined") {
            return;
        }

        const warmChunk = () => LazyNotificationsDrawer.preload();
        const idleCallback = (window as Window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
            cancelIdleCallback?: (id: number) => void;
        }).requestIdleCallback;

        if (typeof idleCallback === "function") {
            const idleId = idleCallback(warmChunk, { timeout: 1200 });
            return () => {
                (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleId);
            };
        }

        const timeoutId = window.setTimeout(warmChunk, 500);
        return () => window.clearTimeout(timeoutId);
    }, [effectiveLoggedIn, notificationsFeatureMounted]);

    React.useLayoutEffect(() => {
        return onOpenChatDrawer((payload) => {
            const nextPayload = payload ?? {};

            if (!effectiveLoggedIn) {
                openAnonymousHeaderAccountAccess("messages", nextPayload);
                return;
            }

            if (chatDrawerReady) {
                return;
            }

            requestChatDrawerOpen(nextPayload);
        });
    }, [chatDrawerReady, effectiveLoggedIn, openAnonymousHeaderAccountAccess, requestChatDrawerOpen]);

    React.useLayoutEffect(() => {
        if (!chatDrawerReady || !pendingChatOpenPayload || typeof window === "undefined") {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            emitOpenChatDrawer(pendingChatOpenPayload);
            setPendingChatOpenPayload(null);
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [chatDrawerReady, pendingChatOpenPayload]);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        // MS-06 — the connection banner has TWO renderers and only one of them may run.
        //
        // `MobileShellNativeUx.setupNativeConnectionBanner` appends a native strip on the same
        // `offline`/`online` events, showing the same `header.connection.*` strings, and is correctly
        // gated: `setupMobileShellNativeUx` returns early outside the shell. This effect had no gate,
        // so inside Capacitor a member going offline saw the sentence twice — once as a `position:
        // fixed` strip on `document.body`, once in the header's top-banner stack, which additionally
        // pushes the header down by `--connection-banner-height`.
        //
        // The gate is here, on the EFFECT, and not on the render: leaving the listeners installed
        // would keep a second five-second reachability poll running beside the native strip's own, on
        // the device where that costs the most. And it is a deletion, not a synchronisation — there is
        // deliberately no shared "is a banner showing" state between the two owners (rule 31: never
        // keep the same value in two places and write it from both). Each environment has exactly one
        // owner, so there is nothing to keep in agreement.
        if (MobileShellRuntime.isNativeShell()) {
            return;
        }

        // Probe reachability before trusting navigator.onLine (see probeServerReachable): ANY HTTP
        // response means we are online, so DevTools throttling / extensions / a stuck-Chrome notifier
        // can no longer flip the banner on while the server is reachable.
        const RECOVERY_POLL_MS = 5000;

        let cancelled = false;
        let pollTimeoutId: number | null = null;

        const clearPoll = () => {
            if (pollTimeoutId != null) {
                window.clearTimeout(pollTimeoutId);
                pollTimeoutId = null;
            }
        };

        const probeReachable = () => probeServerReachable();

        const showReconnected = () => {
            setConnectionState((previous) => previous === "offline" ? "reconnected" : null);
            if (reconnectTimeoutRef.current != null) {
                window.clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = window.setTimeout(() => {
                setConnectionState((previous) => previous === "reconnected" ? null : previous);
                reconnectTimeoutRef.current = null;
            }, 3500);
        };

        const pollForRecovery = async () => {
            const reachable = await probeReachable();
            if (cancelled) {
                return;
            }
            clearPoll();
            if (reachable) {
                showReconnected();
            } else {
                pollTimeoutId = window.setTimeout(() => { void pollForRecovery(); }, RECOVERY_POLL_MS);
            }
        };

        // A suspected-offline signal (offline event, or navigator.onLine === false at mount):
        // only surface the banner if the server is genuinely unreachable; otherwise stay silent.
        const verifySuspectedOffline = async () => {
            const reachable = await probeReachable();
            if (cancelled) {
                return;
            }
            clearPoll();
            if (reachable) {
                // False alarm — clear any stale banner and stop polling.
                setConnectionState((previous) => previous === "offline" ? null : previous);
                return;
            }
            setConnectionState((previous) => previous === "offline" ? previous : "offline");
            pollTimeoutId = window.setTimeout(() => { void pollForRecovery(); }, RECOVERY_POLL_MS);
        };

        const handleOffline = () => {
            if (reconnectTimeoutRef.current != null) {
                window.clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            void verifySuspectedOffline();
        };

        const handleOnline = () => {
            // The online event is a hint too — confirm reachability before clearing/announcing.
            clearPoll();
            void pollForRecovery();
        };

        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);

        // If the browser already claims offline at mount, verify rather than trust it.
        if (typeof navigator !== "undefined" && !navigator.onLine) {
            void verifySuspectedOffline();
        }

        return () => {
            cancelled = true;
            clearPoll();
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
            if (reconnectTimeoutRef.current != null) {
                window.clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, []);

    React.useEffect(() => {
        if (effectiveLoggedIn || typeof window === "undefined" || emailLoginPromptHandledRef.current) {
            return;
        }
        if (isStandaloneAccountAccessPath(window.location.pathname)) {
            return;
        }

        const prompt = readEmailLoginPrompt();
        if (!prompt) {
            return;
        }

        emailLoginPromptHandledRef.current = true;
        // `ACCOUNT-ACCESS-27` (U7): the generic account door, not the messages pair. Every email lane
        // lands here (digests, nudges, notifications), and "Open your inbox" was true of one of them.
        openAccountAccessDialog(localizer, resolveContextualAccountAccessDialogOptions(localizer, {
            ...ACCOUNT_ACCESS_DIALOG_COPY.accountArea,
            surface: "email-entry",
            username: null,
            initialTab: "login",
        }));
        replaceInPageHistoryUrl(prompt.cleanedUrl, "consume-one-shot");
    }, [effectiveLoggedIn, localizer]);

    React.useEffect(() => {
        if (effectiveLoggedIn || typeof window === "undefined" || emailSignupPromptHandledRef.current) {
            return;
        }
        if (isStandaloneAccountAccessPath(window.location.pathname)) {
            return;
        }

        const prompt = readEmailSignupPrompt();
        if (!prompt) {
            return;
        }

        emailSignupPromptHandledRef.current = true;
        // `ACCOUNT-ACCESS-27` (U7): same door as the login prompt above.
        openAccountAccessDialog(localizer, resolveContextualAccountAccessDialogOptions(localizer, {
            ...ACCOUNT_ACCESS_DIALOG_COPY.accountArea,
            surface: "email-entry",
            initialEmail: prompt.email,
            initialTab: "signup",
        }));
        replaceInPageHistoryUrl(prompt.cleanedUrl, "consume-one-shot");
    }, [effectiveLoggedIn, localizer]);

    React.useEffect(() => {
        if (!effectiveLoggedIn || typeof window === "undefined" || notificationPromptHandledRef.current) {
            return;
        }

        const prompt = readNotificationContinuationPrompt();
        if (!prompt) {
            return;
        }

        notificationPromptHandledRef.current = true;
        // NOTIFICATIONS-125 (r178 U46, r179): Conversations left the bell in SV-180 (no drawer tab, no page scope), so a
        // continuation still naming `notificationSection=conversations` is answered by the destination that holds
        // those rows now, `/conversations`, on every width. One replace, as below, so Back returns to the page the
        // member came from. Before this the drawer opened on the busiest bell section with no word about the move.
        if (prompt.section === "conversations") {
            replaceWithSurfaceThatServesIt("/conversations", "retired-surface");
            return;
        }
        // SV-149 A3 + r53 D11: on a phone the drawer is retired on EVERY route, so the instruction is answered by
        // the page. One replace, so Back from the page returns to where the member was before the continuation.
        // A11 (settle round 57, NOTIFICATIONS-77): on desktop too when it names no section, since the drawer has
        // no combined scope and only the page's default is every section.
        if (phoneNotificationsArePageOnly || !prompt.section) {
            const phoneNotificationsUrl = buildPhoneNotificationsPageUrl(prompt);
            replaceWithSurfaceThatServesIt(phoneNotificationsUrl, "retired-surface");
            return;
        }
        // Arm the mount too: under the code split the drawer does not exist yet, and a request nothing
        // renders opens nothing.
        setNotificationsFeatureMounted(true);
        setNotificationOpenRequest((previous) => ({
            key: (previous?.key ?? 0) + 1,
            section: prompt.section,
            readFilter: prompt.readFilter,
        }));
        replaceInPageHistoryUrl(prompt.cleanedUrl, "consume-one-shot");
    }, [phoneNotificationsArePageOnly, effectiveLoggedIn]);

    React.useLayoutEffect(() => {
        if (typeof document === "undefined") {
            return;
        }

        const root = document.documentElement;
        let lastAppliedValue: string | null = null;

        const applyHeight = (heightPx: number) => {
            const nextValue = heightPx > 0 ? `${Math.round(heightPx)}px` : "0px";
            if (nextValue === lastAppliedValue) {
                return;
            }

            lastAppliedValue = nextValue;
            root.style.setProperty(CONNECTION_BANNER_CSS_VAR, nextValue);

            // Several surfaces (the dedicated mobile messages shell, the mobile-header padding)
            // size themselves from where their root lands under this banner stack. They only
            // recompute on viewport resize, so notify them when the banner offset actually changes
            // (e.g. the verify nudge mounts/measures/dismisses) to keep their measured height in
            // sync with the new top offset.
            if (typeof window !== "undefined") {
                window.dispatchEvent(new Event("resize"));
            }
        };

        if (!hasTopBannerStack) {
            applyHeight(0);
            return () => {
                root.style.removeProperty(CONNECTION_BANNER_CSS_VAR);
            };
        }

        const stack = topBannerStackRef.current;
        if (!stack) {
            // Fall back to the known connection-banner height until the stack mounts so content
            // never overlaps the visible banner during the first paint.
            applyHeight(hasConnectionBanner ? CONNECTION_BANNER_HEIGHT_PX : 0);
            return () => {
                root.style.removeProperty(CONNECTION_BANNER_CSS_VAR);
            };
        }

        const measure = () => {
            applyHeight(stack.getBoundingClientRect().height);
        };

        measure();

        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(() => {
                measure();
            });
            observer.observe(stack);
        }

        return () => {
            observer?.disconnect();
            root.style.removeProperty(CONNECTION_BANNER_CSS_VAR);
        };
    }, [hasTopBannerStack, hasConnectionBanner]);

    React.useLayoutEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") {
            updateMainContentPadding("reserve");
            return;
        }

        const state = mobileHeaderStateRef.current;
        state.visible = true;
        state.lastScrollTop = readWindowScrollTop();
        state.scrollAnchorTop = state.lastScrollTop;
        state.lastDirection = null;

        // The padding mode is tracked separately from `visible` precisely because they are no longer
        // the same question: the scroll tuck hides the header and KEEPS the reserve, the keyboard tuck
        // hides it and hands the reserve back. Both are "hidden".
        let paddingMode: MainContentPaddingMode = "reserve";
        // SV-157: the padding writer resolves differently on a phone, so a mode is "applied" per viewport class.
        let paddingAppliedOnPhone = isPhoneViewportNow();

        const setPaddingMode = (nextMode: MainContentPaddingMode) => {
            const onPhone = isPhoneViewportNow();
            if (paddingMode === nextMode && paddingAppliedOnPhone === onPhone) {
                return;
            }

            paddingMode = nextMode;
            paddingAppliedOnPhone = onPhone;
            updateMainContentPadding(nextMode);
        };

        // S7 A87: written only on a visibility CHANGE (and on the reset), never per scroll event.
        // SV-157 A3: `0px` on a phone whatever the state, because there is no phone header to sit under.
        let lastPublishedHeaderOffset: string | null = null;
        const publishHeaderOffset = (visible: boolean) => {
            const nextValue = !isPhoneViewportNow() && visible
                ? `calc(${MOBILE_HEADER_VISIBLE_HEIGHT} + var(${CONNECTION_BANNER_CSS_VAR}, 0px))`
                : "0px";
            if (nextValue === lastPublishedHeaderOffset) {
                return;
            }
            lastPublishedHeaderOffset = nextValue;
            document.documentElement.style.setProperty(HEADER_OFFSET_CSS_VAR, nextValue);
        };

        const setVisible = (nextVisible: boolean, nextPaddingMode: MainContentPaddingMode = "reserve") => {
            setPaddingMode(nextPaddingMode);

            if (state.visible === nextVisible) {
                return;
            }

            state.visible = nextVisible;
            setMobileHeaderVisible(nextVisible);
            publishHeaderOffset(nextVisible);
        };

        const scrollIntent = trackUserScrollIntent(window);
        const syncVisibility = () => {
            const currentScrollTop = readWindowScrollTop();

            // SV-157 A1/A3: a phone has no site header, so none of the tucks below has anything to act on. The
            // state stays "visible" so the transient phone search bar (the only thing `<header>` still renders
            // there) is never mounted half off-screen, and the reserve is the phone value whatever the route.
            if (isPhoneViewportNow()) {
                state.scrollAnchorTop = currentScrollTop;
                state.lastScrollTop = currentScrollTop;
                state.lastDirection = null;
                setVisible(true);
                publishHeaderOffset(true);
                return;
            }

            const currentPathPolicy = getMobileHeaderVisibilityPolicy(window.location.pathname);
            const isLockedVisible = isLockedVisibleSurface(document);

            // Checked BEFORE the policy and the lock, both of which would otherwise pin the header
            // visible on this page (`/messages` is `visibleOnly`, and a focused composer is exactly what
            // `isLockedVisibleSurface` treats as a reason to stay). See shouldKeyboardTuckHeader.
            if (shouldKeyboardTuckHeader(window, document)) {
                state.scrollAnchorTop = currentScrollTop;
                state.lastScrollTop = currentScrollTop;
                state.lastDirection = null;
                // The one caller that genuinely wants the 4.5rem back — see MainContentPaddingMode.
                setVisible(false, "collapse");
                return;
            }

            if (currentPathPolicy === "visibleOnly" || isLockedVisible) {
                state.scrollAnchorTop = currentScrollTop;
                state.lastScrollTop = currentScrollTop;
                state.lastDirection = null;
                setVisible(true);
                return;
            }

            if (currentScrollTop <= MOBILE_HEADER_TOP_ZONE_PX) {
                state.scrollAnchorTop = currentScrollTop;
                state.lastScrollTop = currentScrollTop;
                state.lastDirection = null;
                setVisible(true);
                return;
            }

            // FI-6022: a downward delta with no scrolling input behind it is scroll anchoring (`stepBottomNavTuck`'s rule).
            if (currentScrollTop > state.lastScrollTop && !scrollIntent.hasIntent()) {
                state.scrollAnchorTop += currentScrollTop - state.lastScrollTop;
                state.lastScrollTop = currentScrollTop;
                return;
            }

            const nextDirection = currentScrollTop > state.lastScrollTop
                ? "down"
                : currentScrollTop < state.lastScrollTop
                    ? "up"
                    : state.lastDirection;

            if (state.visible) {
                if (nextDirection === "up") {
                    state.scrollAnchorTop = currentScrollTop;
                } else if (nextDirection === "down") {
                    if (state.lastDirection !== "down") {
                        state.scrollAnchorTop = state.lastScrollTop;
                    }

                    if (currentScrollTop - state.scrollAnchorTop >= MOBILE_HEADER_SCROLL_HIDE_THRESHOLD_PX) {
                        setVisible(false);
                        state.scrollAnchorTop = currentScrollTop;
                    }
                }
            } else if (nextDirection === "up") {
                if (state.lastDirection !== "up") {
                    state.scrollAnchorTop = state.lastScrollTop;
                }

                if (state.scrollAnchorTop - currentScrollTop >= MOBILE_HEADER_SCROLL_REVEAL_THRESHOLD_PX) {
                    setVisible(true);
                    state.scrollAnchorTop = currentScrollTop;
                }
            } else if (nextDirection === "down") {
                state.scrollAnchorTop = currentScrollTop;
            }

            state.lastScrollTop = currentScrollTop;
            state.lastDirection = nextDirection;
        };

        const resetToVisible = () => {
            state.visible = true;
            state.lastScrollTop = readWindowScrollTop();
            state.scrollAnchorTop = state.lastScrollTop;
            state.lastDirection = null;
            setMobileHeaderVisible(true);
            publishHeaderOffset(true);
            setPaddingMode("reserve");
        };

        // Coalesced to one run per frame. `syncVisibility` reads layout (`window.scrollY`) and reads
        // `activeElement`, and touch scrolling fires `scroll` at or above the
        // display refresh rate. It is idempotent and reads live state on every run, so collapsing a
        // burst to its last value changes no behavior and drops the per-event layout read.
        let scrollFrame: number | null = null;
        const onScroll = () => {
            if (scrollFrame !== null) {
                return;
            }

            scrollFrame = requestAnimationFrame(() => {
                scrollFrame = null;
                syncVisibility();
            });
        };

        const onRouteIntent = () => {
            resetToVisible();
        };

        const onViewportChange = () => {
            syncVisibility();
            // SV-157: crossing 768 px changes what the reserve and the offset RESOLVE to without changing the
            // header's state, so both are re-applied here (each is a no-op when nothing changed).
            setPaddingMode(paddingMode);
            publishHeaderOffset(state.visible);
        };

        updateMainContentPadding("reserve");
        publishHeaderOffset(true);
        // BL-140: the contract attribute is rewritten AFTER every navigation event this effect listens
        // to (`EnhancedNavigation` pushes history, then swaps the shell), so it cannot be read on an
        // event. Watch the attribute itself and re-apply whichever mode is current when it changes —
        // an in-app arrival at `/messages` must shed the inline reserve exactly as a full load does.
        const mainContentElement = document.getElementById("seavien-main-content");
        const contractObserver = mainContentElement && typeof MutationObserver !== "undefined"
            ? new MutationObserver(() => {
                updateMainContentPadding(paddingMode);
            })
            : null;
        if (mainContentElement) {
            contractObserver?.observe(mainContentElement, { attributes: true, attributeFilter: [VIEWPORT_CONTRACT_ATTRIBUTE] });
        }
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onViewportChange, { passive: true });
        window.addEventListener("orientationchange", onViewportChange);
        window.addEventListener("focusin", onViewportChange);
        window.addEventListener("focusout", onViewportChange);
        window.addEventListener("pageshow", onRouteIntent);
        window.addEventListener("popstate", onRouteIntent);
        window.addEventListener(BROWSER_NAVIGATION_EVENT_NAME, onRouteIntent);

        const visualViewport = window.visualViewport;
        visualViewport?.addEventListener("resize", onViewportChange);
        visualViewport?.addEventListener("scroll", onViewportChange);

        // SV-157: rotation or split view crossing 768px moves the page between "no header" and "header".
        const phoneViewportQuery = typeof window.matchMedia === "function"
            ? window.matchMedia(MOBILE_VIEWPORT_QUERY_768)
            : null;
        phoneViewportQuery?.addEventListener?.("change", onViewportChange);

        return () => {
            if (scrollFrame !== null) {
                cancelAnimationFrame(scrollFrame);
                scrollFrame = null;
            }
            contractObserver?.disconnect();
            scrollIntent.dispose();
            document.documentElement.style.removeProperty(HEADER_OFFSET_CSS_VAR);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onViewportChange);
            window.removeEventListener("orientationchange", onViewportChange);
            window.removeEventListener("focusin", onViewportChange);
            window.removeEventListener("focusout", onViewportChange);
            window.removeEventListener("pageshow", onRouteIntent);
            window.removeEventListener("popstate", onRouteIntent);
            window.removeEventListener(BROWSER_NAVIGATION_EVENT_NAME, onRouteIntent);
            visualViewport?.removeEventListener("resize", onViewportChange);
            visualViewport?.removeEventListener("scroll", onViewportChange);
            phoneViewportQuery?.removeEventListener?.("change", onViewportChange);
        };
    }, []);

    /*
     * The header's ONE document-level key handler: `/` and `Cmd+K`/`Ctrl+K` focus search (tier-2 finding
     * 28; FI-2555). (Escape used to close the phone search view too; FI-1342 removed that view.)
     *
     * <p><b>`Cmd+K`/`Ctrl+K` is bound (r110 D9, 2026-10-14), reversing d86ef1b19a.</b> That commit left
     * the pair unbound because Ctrl+K is one of Firefox's search-bar shortcuts; the operator chose the
     * binding members carry from Slack, Discord, Notion and GitHub, and Firefox keeps Ctrl+E for its
     * bar. Either modifier is accepted on every platform (no platform sniffing): Cmd+K on Windows and
     * Ctrl+K on a Mac have no competing page-level meaning, and inside an editable field neither key is
     * claimed (Ctrl+K is "kill line" in macOS text fields).
     *
     * <p>Both shortcuts live in this ONE listener. A second `document.addEventListener("keydown")` in
     * this component would double the work every keystroke does on every page, and the two would drift
     * on their guards.
     */
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            /*
             * `isLockedVisibleSurface` is true whenever focus is in an editable element, so this cannot
             * steal a slash from someone typing a URL in the composer or filtering inside a dialog. It
             * keys off the FOCUSED element rather than off whether a dialog is open, which is the part
             * that matters here: a dialog whose text field has focus is covered, and one that is merely
             * open is not typing. The modifier checks are separate and come first:
             * `Ctrl+/` and `Cmd+/` are comment-toggle and accessibility shortcuts elsewhere, so the
             * slash is claimed only bare; `K` is claimed only with exactly Cmd or Ctrl.
             *
             * <p>Focus is read TWICE on purpose, plus a third guard (gate triage `3104de67e3`, 2026-10-15). This
             * listener runs AFTER the target's own handlers, so by the time it reads `document.activeElement` a
             * key the target claimed may already have moved focus: the post editor's Cmd+K opens the link panel
             * and Radix focuses its first button, `activeElement` was no longer editable, and this handler
             * pulled focus to search, which closed the panel. So a key another handler claimed
             * (`defaultPrevented`) is not ours, and the EVENT TARGET (where the member was typing at the
             * keypress) counts as much as wherever focus is now. Same three guards as `ThreadKeyboardShortcuts`.
             */
            const isBareSlash = e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey;
            const isCommandK = (e.key === "k" || e.key === "K")
                && (e.metaKey || e.ctrlKey)
                && !(e.metaKey && e.ctrlKey)
                && !e.altKey
                && !e.shiftKey;
            if ((!isBareSlash && !isCommandK)
                || e.defaultPrevented
                || (e.target instanceof Element && isEditableElement(e.target))
                || isLockedVisibleSurface(document)) {
                return;
            }

            /*
             * Which bar is on screen, read AT THE KEYPRESS rather than subscribed to.
             *
             * `useIsMobile()` would work but would add a second subscription to the persistent header
             * for a value only this handler reads, and re-render the whole chrome on every viewport
             * change (rule 31). A media-query read here costs nothing until someone presses the key,
             * and it cannot be stale.
             *
             * It fails to DESKTOP: the desktop bar is always mounted, so focusing it is the safe
             * answer when `matchMedia` is unavailable. `offsetParent`/`getBoundingClientRect` were the
             * obvious alternatives and are both unusable — jsdom performs no layout, so they report a
             * hidden element in every test as well as on a phone.
             */
            const isPhoneViewport = typeof window.matchMedia === "function"
                && window.matchMedia(MOBILE_VIEWPORT_QUERY_768).matches;

            if (!isPhoneViewport) {
                const desktopInput = desktopSearchInputRef.current;
                if (desktopInput) {
                    e.preventDefault();
                    desktopInput.focus();
                }
                return;
            }

            // Phone (r67 D2): no header search exists there any more, so `/` does what the bottom bar's Search
            // tab does: focus the `/search` page's field when it is on screen, otherwise go to `/search`.
            e.preventDefault();
            if (!focusSearchPageField()) {
                navigateToUrl(SEARCH_TAB_HREF, { source: "keyboard-shortcut-search" });
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // `useCallback` because these are dependencies of the memoized tooltips below, and a handler
    // rebuilt every render would re-arm every one of them — the exact churn they exist to stop.
    /*
     * Bottom navigation badges (slice `1d`; the viewport and keyboard inputs moved up beside
     * `currentPathname` for SV-149, which reads them earlier). The badge records are memoized on their
     * primitive parts so the bar's props are stable by value across the header's own renders.
     */
    const mobileBottomNavMessagesBadge = React.useMemo(
        () => ({ count: effectiveMessagesUnreadCount, showActivityDot: false }),
        [effectiveMessagesUnreadCount],
    );
    const mobileBottomNavConversationsBadge = React.useMemo(
        () => ({ count: conversationsUnreadCount, showActivityDot: conversationsHasPassiveUnread }),
        [conversationsUnreadCount, conversationsHasPassiveUnread],
    );
    const mobileBottomNavNotificationsBadge = React.useMemo(
        () => ({
            count: notificationsActionableCount,
            showActivityDot: notificationsHasPassiveUnread,
            pendingDecisions: notificationsPendingDecisions,
        }),
        [notificationsActionableCount, notificationsHasPassiveUnread, notificationsPendingDecisions],
    );

    /*
     * THE HEADER'S TOOLTIPS ARE MEMOIZED ELEMENTS, AND THAT IS LOAD-BEARING (AGENTS.md rule 31).
     *
     * Radix `SlotClone` builds its composed ref with a bare `composeRefs(...)` called DURING RENDER, so
     * every render of this component hands each `asChild` trigger a brand-new callback ref. React then
     * detaches the old and attaches the new, and Radix `Tooltip` composes a `useState` setter into that
     * ref — so one header render used to cost `setTrigger(null)` + `setTrigger(el)` PER TOOLTIP. With ten
     * of them that is 20 chained updates against React's ceiling of 50, i.e. the header sat three chained
     * renders from crashing with #185, at rest. That is the amplifier behind the 2026-07-23, 07-27, 08-04
     * and 08-20 crashes: each of those fixed a different TRIGGER and left this in place.
     *
     * Holding each tooltip as a memoized ELEMENT makes React bail the whole subtree out of reconciliation
     * when nothing it reads has changed, so the Slot never re-runs and no ref is recomposed. A tooltip
     * whose own data changes still costs its one cycle, which is correct and bounded.
     *
     * ⚠️ EVERY DEPENDENCY LISTED HERE MUST BE STABLE BY VALUE OR IDENTITY. A handler declared inline in
     * this component (rather than via `useCallback`) re-arms its tooltip on every render and silently
     * restores the amplifier for that control. Guarded by `headerTooltipRefChurn.spec.tsx`, which fails
     * the build if an unrelated header render costs any ref cycles at all. (This comment named
     * `MainHeader.refChurn.spec.tsx` until 2026-08-23; no such file has ever existed in this repo, so
     * anyone following the pointer to check the invariant found nothing. The drawer half is
     * `drawerPresenceRefChurn.spec.tsx`.)
     *
     * ⚠️ BOTH ref-churn guards are SYNTHETIC — they render toy `<Parent>` / `<Header>` components that
     * characterise the MECHANISM, and neither mounts a real header subtree. So they cannot fail when a
     * real component gains an unmemoized `asChild` trigger; that is how two of them reached
     * `NotificationsView`'s drawer header in the 2026-08-23 promotion behind a green build. When you add
     * an `asChild` trigger anywhere in the header, the guard is this comment, not the suite.
     *
     * A stable ref on the CHILD does not help — `composeRefs` reallocates regardless — and a single
     * memoized wrapper taking the control as `children` does not help either, because `children` is a
     * fresh element every render and defeats the shallow compare.
     */
    const logoTooltip = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <a
                    href="/"
                    data-playwright-id="main-header-home-logo"
                    className="ml-2 rounded-2xl px-2 py-2 transition-[background-color,box-shadow]  sm:ml-4 sm:px-2.5"
                    aria-label={localizer("header.tooltip.home")}
                >
                    {/* FI-766 (round 50 A24): below 380 px the glyph steps to `h-8` (102.6 -> 91.2 px wide)
                        so the iPhone SE row leaves the search pill its 44 px; every control is kept. */}
                    <LogoIcon className={"h-9 max-[380px]:h-8"}></LogoIcon>
                </a>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {localizer("header.tooltip.home")}
            </TooltipContent>
        </Tooltip>
    ), [localizer]);

    /*
     * `CREATE-PUBLISH-10`, operator decision D7 — the compose control is ROUTE-AWARE.
     *
     * Off the composer it is unchanged: `/create/discussion` (`reuseEmptyOrCreate`), labelled
     * "Create". On the composer it means **"start another"** and points at `/create/discussion/new`
     * (`forceCreateNew`), which is the only thing that reliably mints a second draft — and which was
     * linked from no chrome control at all until this change.
     *
     * **Why this one navigates while the messages control beside it goes inert.** `MESSAGES-2` made
     * the messages trigger do nothing on `/messages` because the page already renders the inbox it
     * would open: there was no second destination. Here there IS one — a different document — so the
     * control keeps a real job and only its destination and its name change.
     *
     * **No `aria-current="page"`, deliberately, and that is not an oversight copied from the messages
     * control.** This link does not point at the current page; announcing that it does would be false
     * to a screen reader at exactly the moment the label is telling everyone else the opposite. The
     * route cue here is the LABEL, which is also the only cue a phone gets — the tooltip does not
     * exist on touch (`CREATE-PUBLISH-8`, `-21`).
     *
     * **Unsaved work is protected by inheritance, not by anything here.** `CREATE-PUBLISH-9` closed by
     * putting the leave veto at the navigation seam (`NavigationLeaveGuardRegistry`, consulted by
     * `EnhancedNavigation.handleDocumentClick`), so pressing this off a dirty draft raises the
     * composer's own confirm exactly as its other exits do.
     *
     * `create.drafts-list.new-draft-button` is REUSED, not minted (rule 11): it is already the drafts
     * dialog's own word for this same action in all 25 catalogs, so the two places that start a second
     * draft say the same thing.
     */
    const createLabel = onComposerPath ? startAnotherDraftLabel : baseCreateLabel;
    const createTooltip = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={SquarePen}
                    href={onComposerPath ? CREATE_DISCUSSION_FORCE_NEW_PATH : CREATE_DISCUSSION_PATH}
                    // `max-sm:ml-0`: on a phone this `ml-2` stacked on the row's `gap-2` (16 px before the
                    // cluster vs 8 px between its controls), and at 393 pt the search pill, floored at
                    // 44 px, spilled left over the wordmark link (iOS critique, 2026-09-28).
                    extraClasses="ml-2 max-sm:ml-0"
                    dataPlaywrightId="main-header-create-discussion"
                    // `createLabel`, the same string the tooltip beside it renders. It announced "Post"
                    // (`create.create-template.new-tab`) while its only visible label said "Create", so
                    // the spoken name and the seen name were different words for the same control, and
                    // on a phone the spoken one is the only name there is (CREATE-PUBLISH-21).
                    aria-label={createLabel}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {createLabel}
            </TooltipContent>
        </Tooltip>
    ), [createLabel, onComposerPath]);

    /*
     * SV-169 `1b` (work order A2, 2026-10-10): the Conversations destination, an icon button immediately BEFORE
     * Messages with the same treatment. Its glyph is `MessageCircleReply` (FI-1866, r92): the original
     * `MessageSquareText` was a square bubble beside Messages' `MessagesSquare` and the two read as one. A plain link to the
     * page, never a drawer, so nothing lazy mounts here and there is no warm-up ladder to get wrong (rule 26).
     *
     * <p><b>SV-180 D3 (2026-10-16, reverses SV-169 D1 here): the button carries the conversation notifications.</b>
     * The bell's rendering exactly (`HeaderButton`'s count, else its dot), on the hooks
     * `main-header-conversations-count` / `-activity-dot`. <b>SV-182 D1/D2:</b> the number is the hub's cards with
     * something new (the server's `conversationsBadge`, via `conversationsNotificationBadge`), and the hub's All tab
     * draws the same number; since SV-185 `2a` every other tab draws its own per-tab count (there is no New tab).
     * `aria-current="page"` on the page itself, like the messages control. Signed-in only (A1): the page is login-only.
     * On a phone the member's door is the bottom bar's Conversations tab (SV-180 D2: third, after Create).
     */
    const isOnConversationsPage = currentPathname === CONVERSATIONS_PAGE_PATH
        || currentPathname.startsWith(`${CONVERSATIONS_PAGE_PATH}/`);
    const conversationsTooltip = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={MessageCircleReply}
                    href={CONVERSATIONS_PAGE_PATH}
                    notificationCount={conversationsUnreadCount}
                    showActivityDot={conversationsHasPassiveUnread}
                    extraClasses="ml-2 main-header-conversations-button"
                    aria-label={conversationsLabel}
                    aria-current={isOnConversationsPage ? "page" : undefined}
                    dataPlaywrightId="main-header-conversations"
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {conversationsLabel}
            </TooltipContent>
        </Tooltip>
    ), [conversationsLabel, conversationsHasPassiveUnread, conversationsUnreadCount, isOnConversationsPage]);

    const messagesTooltipPending = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={MessagesSquare}
                    notificationCount={effectiveMessagesUnreadCount}
                    extraClasses="ml-2 main-header-messages-button"
                    aria-label={messagesLabel}
                    aria-current={isOnMessagesPage ? "page" : undefined}
                    dataPlaywrightId="main-header-messages"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        requestChatDrawerOpen();
                    }}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {messagesLabel}
            </TooltipContent>
        </Tooltip>
    ), [effectiveMessagesUnreadCount, messagesLabel, isOnMessagesPage, requestChatDrawerOpen]);

    const messagesTooltipIdle = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={MessagesSquare}
                    notificationCount={effectiveMessagesUnreadCount}
                    extraClasses="ml-2 main-header-messages-button"
                    aria-label={messagesLabel}
                    aria-current={isOnMessagesPage ? "page" : undefined}
                    dataPlaywrightId="main-header-messages"
                    onFocus={requestDesktopChatFeatureMount}
                    onMouseEnter={requestDesktopChatFeatureMount}
                    onTouchStart={requestDesktopChatFeatureMount}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        requestChatDrawerOpen();
                    }}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {messagesLabel}
            </TooltipContent>
        </Tooltip>
    ), [effectiveMessagesUnreadCount, messagesLabel, isOnMessagesPage, requestDesktopChatFeatureMount, requestChatDrawerOpen]);

    /*
     * The bell that is on screen BEFORE the notifications chunk exists.
     *
     * <p>⚠️ This is a deliberate second copy of the bell inside `NotificationsView`, and the copy is
     * structural rather than lazy: the real one must be a `DrawerTrigger asChild` child of the
     * `<Drawer>`, and this one must not be, so they cannot share a component. `MessagesDrawer` has
     * carried the same three-copy shape (idle here, pending here, real in the drawer) since the chat
     * split. **What must not drift is the DERIVATION, and it does not**: both bells read
     * `actionableNotificationCount`, `hasPassiveUnreadNotifications` and `hasPendingDecisions` off the
     * same `HeaderInboxStore` snapshot, so the badge cannot change value when the drawer mounts. If
     * you change the badge semantics, change both — `NotificationsBellBadge.spec.tsx` pins the
     * drawer's copy and `MainHeaderNotificationsBell.spec.tsx` pins this one.
     *
     * <p>Badge semantics are `NOTIFICATIONS-15` + the 2026-08-19 header-badge design: the number counts
     * what the member has to DO, the dot marks what merely happened to them, and
     * `data-pending-decisions` is a THIRD marker (`NOTIFICATIONS-44`) that composes with either; since
     * `HOME-FEED-58` (r69 A8) it paints as `HeaderButton`'s decision dot, never a ring (a focus lookalike).
     *
     * <p>MEMOIZED ELEMENT — see the tooltip block above. Every dependency is a primitive or a `[]`
     * `useCallback`.
     */
    const notificationsTooltipIdle = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={Bell}
                    notificationCount={notificationsActionableCount}
                    showActivityDot={notificationsHasPassiveUnread}
                    data-pending-decisions={notificationsPendingDecisions ? "true" : "false"}
                    showDecisionDot={notificationsPendingDecisions}
                    extraClasses="ml-2 main-header-notifications-button"
                    aria-label={notificationsAccessibleName}
                    dataPlaywrightId="main-header-notifications"
                    onFocus={armNotificationsFeature}
                    onMouseEnter={armNotificationsFeature}
                    onTouchStart={armNotificationsFeature}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        requestNotificationsDrawerOpen();
                    }}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {notificationsLabel}
            </TooltipContent>
        </Tooltip>
    ), [
        notificationsActionableCount,
        notificationsHasPassiveUnread,
        notificationsPendingDecisions,
        notificationsLabel,
        notificationsAccessibleName,
        armNotificationsFeature,
        requestNotificationsDrawerOpen,
    ]);

    /*
     * The Suspense fallback: the chunk has been asked for and has not arrived. Identical to the idle
     * bell minus the arming handlers, which have already done their job. It still QUEUES a click
     * (`requestNotificationsDrawerOpen` bumps the open request), so a member who clicks during the
     * in-flight window gets the drawer rather than nothing — rule 32's "JS may only improve on what
     * was there" applied to the loading window itself.
     */
    const notificationsTooltipPending = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={Bell}
                    notificationCount={notificationsActionableCount}
                    showActivityDot={notificationsHasPassiveUnread}
                    data-pending-decisions={notificationsPendingDecisions ? "true" : "false"}
                    showDecisionDot={notificationsPendingDecisions}
                    extraClasses="ml-2 main-header-notifications-button"
                    aria-label={notificationsAccessibleName}
                    dataPlaywrightId="main-header-notifications"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        requestNotificationsDrawerOpen();
                    }}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {notificationsLabel}
            </TooltipContent>
        </Tooltip>
    ), [
        notificationsActionableCount,
        notificationsHasPassiveUnread,
        notificationsPendingDecisions,
        notificationsLabel,
        notificationsAccessibleName,
        requestNotificationsDrawerOpen,
    ]);

    /*
     * r53 D11: the phone bell where the bottom nav stands down. The idle bell's badges, name and hook, but an
     * anchor to the page instead of a drawer trigger, and no arming handlers (there is no drawer to warm on a
     * phone). The document's link handling navigates it, so no history write happens here (rule 33).
     */
    const notificationsPageLink = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={Bell}
                    href={PHONE_NOTIFICATIONS_PAGE_PATH}
                    notificationCount={notificationsActionableCount}
                    showActivityDot={notificationsHasPassiveUnread}
                    data-pending-decisions={notificationsPendingDecisions ? "true" : "false"}
                    showDecisionDot={notificationsPendingDecisions}
                    extraClasses="ml-2 main-header-notifications-button"
                    aria-label={notificationsAccessibleName}
                    dataPlaywrightId="main-header-notifications"
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {notificationsLabel}
            </TooltipContent>
        </Tooltip>
    ), [
        notificationsActionableCount,
        notificationsHasPassiveUnread,
        notificationsPendingDecisions,
        notificationsLabel,
        notificationsAccessibleName,
    ]);

    const profileTooltip = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <ProfileImage localizer={localizer}
                    imageUrl={profileImageUrlState ?? undefined}
                    username={effectiveUsername ?? undefined}
                    className="size-10"
                    mode="user"
                    hoverable={true}
                    data-playwright-id="main-header-profile-image"
                    // The Radix trigger's onClick makes ProfileImage render its <button> branch, and a
                    // letter tile's only content is an aria-hidden icon: unnamed (axe button-name,
                    // a11y-six-paths first run, 2026-09-30). The avatar opens the main sidebar, the same
                    // destination as the anonymous Menu button, so it takes the same name.
                    aria-label={menuLabel}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4} translate="no">
                {effectiveUsername}
            </TooltipContent>
        </Tooltip>
    ), [profileImageUrlState, effectiveUsername, menuLabel]);

    /*
     * ⛔ THREE CONTROLS USED TO LIVE HERE, AND THEY ARE NOT COMING BACK. `LANDING-54` /
     * `ACCOUNT-ACCESS-3`, operator decision D5, 2026-08-26.
     *
     * `anonCreateTooltip`, `anonMessagesTooltip` and `anonNotificationsTooltip` were unlabelled
     * member chrome shown to anonymous visitors, and all three did the SAME thing when tapped: open
     * the account-access dialog with a different context string. On a 412px phone that spent three of
     * the header's five 44px controls on one destination, which is precisely why the earlier attempt
     * at a labelled account entry concluded there was no room for one and shipped it `sm:` and up.
     *
     * They were DELETED rather than gated. A gate is a predicate someone loosens later; an absent
     * control is absent. What replaces all three is the one labelled pill below, which now renders at
     * every viewport on the width they were occupying.
     *
     * ⚠️ `openAnonymousHeaderAccountAccess("messages", …)` still exists and is still called — by the
     * programmatic `onOpenChatDrawer` listener, not by any control. That path carries a message intent
     * raised elsewhere in the app into the login continuation, and it is a different feature from the
     * glyph this decision removed.
     */

    /**
     * LANDING-17, wave-8 decision D9(a): the labelled account entry beside the icon set.
     *
     * <p>A missing LABEL, not a missing feature — the continuation plumbing already exists and every
     * icon in this cluster already routes into the same account-access dialog. What an anonymous
     * visitor got was four unlabelled controls, no avatar, and the only labelled account entry
     * ("Account") behind the hamburger, so nothing on screen said an account was even a thing here.
     *
     * <p><b>It is deliberately not a CTA.</b> Removing the header login/signup CTA was a recorded
     * product decision, so this is the product's quiet outline pill: bordered, card-filled, plain
     * label, no primary fill and no promotional copy. It says where the door is; it does not sell.
     *
     * <p><b>Every viewport, since 2026-08-26 (`LANDING-54`, operator decision D5).</b> This used to be
     * `hidden … sm:inline-flex`, and the reason was written here as an authorisation problem rather
     * than a layout one: on a 412px phone the anonymous cluster already carried five 44px controls
     * plus the wordmark, "so a labelled pill cannot join it without removing a control — which D9 does
     * not authorise". <b>D5 is that authorisation.</b> Three of those five controls all opened this
     * same dialog and were deleted, so the width exists now and the pill takes it. The wordmark ends
     * at ~117 CSS px and the row has ~278 px to give; search (44) + this pill (~85) + the menu (44)
     * plus two 8px gaps fits with room to spare.
     *
     * <p><b>The one place it is still `sm:` and up: a standalone account-access route.</b> That is
     * `ACCOUNT-ACCESS-3`'s surviving remainder — on `/login` this pill opens a modal copy of the form
     * already on screen — and it is PARKED, because suppressing it collides with `ACCOUNT-ACCESS-17`,
     * which cites this pill as the desktop fallback route back to the sign-in form from
     * `/reset_account`. Rendering it at mobile there too would widen a parked defect from one
     * breakpoint to both, which D5 does not ask for and nobody has decided. So the mobile appearance
     * is D5's dividend and it is not claimed on those routes.
     *
     * <p><b>`/login` and `/signup`: no pill at all (round 100 D57, 2026-10-13).</b> The `-17` sequencing
     * is settled: on those two routes the pill can only open a modal copy of the form the page already
     * IS, so it is not rendered at any width. `/reset_account` KEEPS the desktop pill, because `-17`
     * cites it as the route back to sign-in from there; the other standalone routes keep it too (D57
     * names only these two). So the route condition above still guards the mobile appearance on the
     * routes that keep the pill; do not "simplify" either condition away.
     *
     * <p>Reuses `account-access.login`, the same word the dialog's own login tab uses, so the control
     * and the thing it opens say the same thing in all 25 locales. No new label key.
     */
    const hideAnonSignInControl = currentPathname === "/login" || currentPathname === "/signup";
    const anonSignInControl = React.useMemo(() => hideAnonSignInControl ? null : (
        <button
            type="button"
            data-playwright-id="main-header-sign-in"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openAnonymousHeaderAccountAccess("sign-in");
            }}
            className={`ml-2 min-h-11 shrink-0 items-center whitespace-nowrap rounded-full border border-control-boundary bg-card px-4 text-sm font-medium text-[var(--text-main)] standard-hover touch-manipulation focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/70 focus-visible:ring-[3px] ${onStandaloneAccountAccessPath ? "hidden sm:inline-flex" : "inline-flex"}`}
        >
            {localizer("account-access.login")}
        </button>
    ), [hideAnonSignInControl, localizer, onStandaloneAccountAccessPath, openAnonymousHeaderAccountAccess]);

    const anonMenuTooltip = React.useMemo(() => (
        <Tooltip>
            <TooltipTrigger asChild>
                <HeaderButton
                    icon={Menu}
                    extraClasses="ml-2 main-header-main-sidebar-button"
                    dataPlaywrightId="main-header-menu"
                    aria-label={menuLabel}
                />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={4}>
                {localizer("header.tooltip.menu")}
            </TooltipContent>
        </Tooltip>
    ), [menuLabel, localizer]);

    /*
     * ⚠️ MEMOIZED ELEMENTS. DO NOT RENDER EITHER DRAWER INLINE IN THE JSX BELOW.
     *
     * Both drawers hold a vaul `Root` → Radix `Dialog` → `DialogPortal` → `Presence` → `Slot` chain, and
     * `SlotClone` composes its ref with a bare `composeRefs(...)` called DURING RENDER. So a render of
     * THIS component that reaches either drawer costs a detach/attach cycle on the scrim and the panel,
     * and every attach runs `usePresence`'s `setNode` — a commit-phase `setState` chained onto the render
     * that caused it. This component re-renders on scroll (`mobileHeaderVisible`), on every header-inbox
     * notify, on pathname change and on viewport change, so without these boundaries the open drawer's
     * chained-update cost is paid on all of them. React crashes the island at 50 (#185), and the
     * 2026-08-22 production stacks are inside exactly this chain.
     *
     * Guard: `scripts/check-drawer-surface-memoization.mjs`; arity in `drawerPresenceRefChurn.spec.tsx`.
     */
    // The Suspense wrapper is deliberately OUTSIDE this memo. Its `fallback` is `messagesTooltipPending`,
    // whose own deps include the badge counts — so folding it in here would rebuild the drawer element on
    // every unread change and re-render vaul's Root for a value only the pending TRIGGER reads. Kept out,
    // a badge change re-creates the cheap Suspense element while its child stays reference-identical, and
    // React bails the whole drawer out of reconciliation.
    const messagesDrawerElement = React.useMemo(() => (
        <LazyMessagesDrawer
            localizer={localizer}
            onReady={handleChatDrawerReady}
        />
    ), [handleChatDrawerReady, localizer]);

    // Suspense stays OUTSIDE this memo for the same reason it does for the messages drawer: its
    // fallback's deps include the badge derivations, so folding it in would rebuild the drawer element
    // on every unread change and re-render vaul's Root for a value only the fallback TRIGGER reads.
    const notificationsDrawerElement = React.useMemo(() => (
        <LazyNotificationsDrawer
            localizer={localizer}
            openRequest={notificationOpenRequest}
        />
    ), [localizer, notificationOpenRequest]);

    return (
        <SidebarProvider defaultOpen={false}>
            <>
                {hasTopBannerStack ? (
                    <div
                        ref={topBannerStackRef}
                        className="fixed left-0 top-0 z-layer-chrome flex w-full flex-col"
                        style={{ paddingTop: "env(safe-area-inset-top)" }}
                    >
                        {hasConnectionBanner ? (
                            <div
                                className="flex w-full items-center justify-center border-b border-border/60 px-4 text-center text-xs font-medium text-[var(--text-inverse)] shadow-sm"
                                style={{
                                    height: CONNECTION_BANNER_HEIGHT_CSS,
                                    backgroundColor: connectionState === "offline"
                                        ? "var(--connection-banner-offline)"
                                        : "var(--connection-banner-online)",
                                }}
                                role="status"
                                aria-live="polite"
                                // U39 (FI-3669, r156): persistent chrome holds still across a route transition.
                                data-persistent-chrome="connection-banner"
                            >
                                {connectionState === "offline"
                                    ? localizer("header.connection.offline")
                                    : localizer("header.connection.online")}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {headerRendered ? (
                <header
                    className="fixed left-0 z-layer-chrome flex w-full items-center justify-between overflow-hidden border-b border-divider bg-card/95 px-0 py-2 shadow-(--surface-shadow-soft) backdrop-blur-xl"
                    data-mobile-header-state={!mobileHeaderVisible ? "hidden" : "visible"}
                    style={{
                        top: `var(${CONNECTION_BANNER_CSS_VAR}, 0px)`,
                        willChange: "transform",
                        transitionProperty: "transform",
                        ...getMobileHeaderVisibilityStyle(mobileHeaderVisible),
                    }}>

                    {/* The header row. SV-157 A1: never on a phone (the header itself is unmounted there). */}
                    {isPhoneViewport ? null : (
                    <div
                        className="w-full min-w-0 flex justify-between items-center gap-2">
                        {logoTooltip}

                        {/* Desktop search (hidden on small screens).
                            No Tooltip wrapper: a `side="bottom"` tooltip on a text input opens into the
                            exact space the typeahead dropdown needs, so typing produced a black "Search"
                            bubble over the suggestions. It was redundant anyway — the input carries both
                            a visible placeholder and an aria-label — and per AGENTS.md rule 31 the
                            `asChild` trigger recomposed a callback ref on every header render, which is
                            the render-churn pattern behind the React #185 header crashes. */}
                        {/* SV-149 A6 (landing review r3): `sm:block` shows this field from 640 px while
                            "phone" is < 768 px, so a 640-767 px viewport with the bottom nav up would keep a
                            header Search the bar already owns. The `/` shortcut only reads this ref off-phone. */}
                        {bottomNavOwnsTheDoors ? null : (
                            <div className="hidden sm:block flex-1 min-w-0 max-w-150 px-2">
                                <HeaderSearchBar
                                    ref={desktopSearchInputRef}
                                    searchQuery={effectiveSearchQuery}
                                    searchType={effectiveSearchType}
                                    placeholder={searchPlaceholder}
                                    ariaLabel={searchLabel}
                                    localizer={localizer}
                                />
                            </div>
                        )}

                        {/* FI-766 (round 50 A24): `mr-2` below `sm`, not `mr-4`. The header row is `px-0`,
                            so this margin is the row's whole right inset: 8 px matches the wordmark link's
                            `ml-2` and returns 8 px to the search slot. Not `mr-0`, which would put the
                            avatar on the bezel. */}
                        <div className="mr-2 flex shrink-0 items-center rounded-full border border-transparent bg-transparent sm:mr-8 sm:gap-4" data-header-cluster="true">
                            {effectiveLoggedIn ? (
                                <>
                                    {/* SV-149 R1: Create, Messages and the Bell are the bottom nav's on a
                                        phone (see `bottomNavOwnsTheDoors`), and the avatar stays. */}
                                    {bottomNavOwnsTheDoors ? null : createTooltip}

                                    {/*
                                      * The boundaries below are CONTAINMENT, not decoration. A chunk
                                      * that never arrives makes `React.lazy` reject during render, and
                                      * that rejection used to travel all the way to
                                      * `MountErrorBoundary` at the island root — so a missing 5 KB
                                      * drawer cost the member the entire header, including search,
                                      * navigation and the profile menu. It did exactly that five times
                                      * on 2026-08-31 while a rolling deploy had old and new tasks
                                      * serving side by side.
                                      *
                                      * The fallback is the IDLE copy, which is the same control the
                                      * member was already looking at, badged off the same
                                      * `HeaderInboxStore` snapshot. It is inert for the rest of the
                                      * page view — the drawer it would open is the thing that failed to
                                      * load — and that is the accepted trade: a control that does not
                                      * open beats a header that is not there (rule 32).
                                      */}
                                    {/* SV-169 A2: Conversations, immediately before Messages; a plain link. */}
                                    {bottomNavOwnsTheDoors ? null : conversationsTooltip}

                                    {bottomNavOwnsTheDoors ? null : chatFeatureMounted ? (
                                        <LazyControlBoundary controlName="messages-drawer" fallback={messagesTooltipIdle}>
                                            <React.Suspense fallback={messagesTooltipPending}>
                                                {messagesDrawerElement}
                                            </React.Suspense>
                                        </LazyControlBoundary>
                                    ) : (
                                        messagesTooltipIdle
                                    )}

                                    {bottomNavOwnsTheDoors ? null : phoneNotificationsArePageOnly ? notificationsPageLink : notificationsFeatureMounted ? (
                                        <LazyControlBoundary controlName="notifications-drawer" fallback={notificationsTooltipIdle}>
                                            <React.Suspense fallback={notificationsTooltipPending}>
                                                {notificationsDrawerElement}
                                            </React.Suspense>
                                        </LazyControlBoundary>
                                    ) : (
                                        notificationsTooltipIdle
                                    )}

                                    {/* SV-135 S7 (D6): the avatar is 40px, and this wrapper carries the 44px
                                        coarse-pointer target. `touch-hit-44` cannot sit on the avatar
                                        itself: ProfileImage clips with `overflow-hidden`, which would clip
                                        the transparent hit slug too. `main-header-profile-image` moves here
                                        with it, because the sidebar's ToggleListener matches taps with
                                        `closest()` on that class, and a tap on the slug targets this div. */}
                                    <div className="main-header-profile-image relative ml-2 inline-flex touch-hit-44">
                                        {profileTooltip}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* D5, 2026-08-26: search + ONE labelled account control +
                                        the account menu. The three icon controls that all opened the
                                        same dialog are gone (see the block above the pill). The
                                        account menu STAYS: it is the one anonymous chrome control
                                        that is not a second door to this page, and removing it would
                                        take the language and theme controls with it. */}
                                    {anonSignInControl}

                                    {anonMenuTooltip}
                                </>
                            )}
                        </div>
                    </div>
                    )}

                </header>
                ) : null}

                <MainSideBarInner {...mainSideBarProps} />

                {/*
                  * Phone-only bottom navigation, phase 1 (slice `1d`, 2026-09-14; design
                  * `designs/00-inbox/2026-05-28-phone-only-bottom-navigation-design.md`). Mounted by
                  * MOUNT, not by a `md:hidden` copy: `isPhoneViewport` is the sanctioned media-query
                  * store, so above phone width the bar does not exist and no hook resolves twice. The
                  * badge values are the SAME selectors the header's own controls read above (rule 31's
                  * multi-renderer obligation), and nothing here adds a subscription the header did not
                  * already hold: the pathname and the keyboard inset are the two inputs the visibility
                  * runtime above already tracks.
                  */}
                {isPhoneViewport ? (
                    <MobileBottomNav
                        localizer={localizer}
                        loggedIn={effectiveLoggedIn}
                        pathname={currentPathname}
                        visible={mobileBottomNavVisible}
                        createHref={onComposerPath ? CREATE_DISCUSSION_FORCE_NEW_PATH : CREATE_DISCUSSION_PATH}
                        conversationsBadge={mobileBottomNavConversationsBadge}
                        messagesBadge={mobileBottomNavMessagesBadge}
                        notificationsBadge={mobileBottomNavNotificationsBadge}
                        profileImageUrl={profileImageUrlState}
                        username={effectiveUsername}
                    />
                ) : null}
            </>
        </SidebarProvider>
    );
}

/**
 * The island's `data-component`, and the key its SSR snapshot is filed under.
 *
 * <p>Only the fallback for a caller that mounted a container without the attribute — in which case
 * `MountErrorBoundary`'s own `componentName` is `undefined` too, so neither side restores and the two
 * stay consistent. Real containers always carry it (`main-header-react.jte`).
 */
const MAIN_HEADER_COMPONENT_NAME = "MainHeader";

const mountMainHeaderIsland = createReactMount<MainHeaderProps & { localizer: Localizer }>({
    name: "mountMainHeader",
    getProps: ({ dataset, localizer }) => ({
            loggedIn: dataset.loggedIn === "true",
            profileImageUrl: dataset.profileImageUrl && dataset.profileImageUrl.length > 0 ? dataset.profileImageUrl : null,
            username: dataset.username && dataset.username.length > 0 ? dataset.username : null,
            searchQuery: dataset.searchQuery ?? "",
            searchType: dataset.searchType ?? "discussions",
            localizer,
        }),
    render: (props) => <MainHeader {...props} />,
});

/**
 * ⚠️ THE SNAPSHOT IS TAKEN HERE, AND IT HAS TO BE (rule 32, added 2026-08-24).
 *
 * <p>`main-header-react.jte` server-renders a `hidden` fallback chrome inside this island — a wordmark
 * home link and, for a signed-in member, `data-playwright-id="main-header-profile-image"` — so that a
 * React #185 crash of this component degrades to the server's chrome instead of to
 * `RecoverableIslandFallback`, which inside a fixed `overflow-hidden` header reads to a member as the
 * header having vanished. `MountErrorBoundary` restores it only when `ssrSnapshot` has something filed
 * under this island's component name.
 *
 * <p>The restore helper is deliberately NOT named with parentheses anywhere in this file:
 * `ssrRestoreMarkerContract.spec.ts` keys on a call-shaped mention, and this file CAPTURES rather than
 * restores, so appearing on its pin list would be wrong.
 *
 * <p><b>Nothing was ever recording it.</b> `captureSsrSnapshot` has exactly one production caller,
 * `ReactMounter.mountAll`, and that call sits BELOW the `STATIC_MOUNT_MAP` early return this island
 * takes — the header is static precisely because it is chrome on almost every page and must not cost a
 * per-navigation chunk fetch. So from 2026-08-22 the recovery markup shipped, `createRoot()` discarded
 * it on every mount, and a crash still produced the error card, no `data-island-render-failed` marker
 * (`MountErrorBoundary` writes that only on the restore branch) and a 27,000 ms
 * `waitForAuthenticatedShellUi` timeout naming nothing. Three consecutive devo gates read that as an
 * unexplained hang on `/messages`.
 *
 * <p>It runs before `createReactMount` reaches `mountReactNode`, which is the last moment the server
 * markup still exists — `createRoot()` discards the container's children. The key is read off the
 * element rather than written as a literal because `MountErrorBoundary` looks the snapshot up under
 * `element.dataset.component`; deriving both from one source is what makes them agree by construction.
 *
 * <p><b>The dispatcher now captures too, and this call deliberately stays (2026-08-24, second pass).</b>
 * `ReactMounter.mountAll` hoisted its capture ABOVE the `STATIC_MOUNT_MAP` early return, so every static
 * island inherits rule 32's second door instead of hand-patching itself into it. That makes this the
 * second capture of the same key — provably inert, because both run before `createReactMount` reaches
 * `mountReactNode`, on the same element, under a key derived from the same attribute, and the capture is
 * a map write of `innerHTML` (or a per-key delete when the container is text-free): the second call
 * writes the identical string. It is kept only for this island, whose degradation is unbounded (chrome
 * on almost every page; its crash removes all navigation and presents as a 27,000 ms auth-predicate
 * timeout naming nothing). <b>Do not copy this into a new island</b> — the dispatcher is the mechanism,
 * and capture-by-opt-in is exactly the pattern that produced this defect.
 *
 * <p>Design: designs/01-now/2026-08-24-main-header-ssr-recovery-was-never-captured.md, then
 * designs/01-now/2026-08-24-rule-32-static-island-capture-and-the-degraded-marker.md
 * Guard: `mainHeaderSsrRecovery.spec.tsx` (verified red by removing this call); the dispatcher's own
 * half is pinned separately in `ReactMounter.spec.ts`, with this module mocked out.
 */
export const mountMainHeader: ReactMount = (element, localizer) => {
    captureSsrSnapshot(element.dataset.component ?? MAIN_HEADER_COMPONENT_NAME, element);
    mountMainHeaderIsland(element, localizer);
};
